import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Button, InputNumber, Input, Table, Empty, Spin, message, DatePicker, Select, Tabs, Tag, Space, Alert, Modal, Segmented, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, DownloadOutlined, ClockCircleOutlined, ReloadOutlined, SaveOutlined, BookOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { getMLGDAMAllocation, getGDAMAllocationAnalysis, getPlants, getGDAMOverrideRules, saveGDAMOverrideRules, updatePlant, getGDAMBidPriceOverrideRules, saveGDAMBidPriceOverrideRules, getWhatIfAllocationRules, saveWhatIfAllocationRules, getWhatIfBookmarks, saveWhatIfBookmark, deleteWhatIfBookmark, submitScenarioBids, getBidSubmissionHistory } from '../services/api';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, LabelList, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const IS_DAY_AHEAD_READY = true;

// Helper to format rupees in Indian standard style
const formatRupees = (val) => {
  if (val == null || isNaN(val)) return '₹ 0';
  const rounded = Math.round(val);
  return '₹ ' + rounded.toLocaleString('en-IN');
};

const formatComparisonValue = (val, metric) => {
  if (val == null || isNaN(val)) return '₹ 0';
  const num = Number(val);
  const isDeduction = metric === 'Charges & Commission' || metric === 'DSM Penalty';
  const absNum = Math.abs(num);
  let sign = '';
  if (isDeduction) {
    if (absNum > 0.000001) sign = '-';
  } else {
    sign = num < 0 ? '-' : '';
  }
  if (absNum >= 10000) {
    return `${sign}₹ ${(absNum / 1000000).toFixed(2)} M`;
  }
  return `${sign}₹ ${absNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatVolume = (val) => {
  if (val == null || isNaN(val)) return '0.0 MW';
  return `${Number(val).toFixed(1)} MW`;
};

// --- Scenario Comparison helpers ---
const SCENARIO_ORDER = ['Ideal', 'Realistic', 'Actual', 'ML', 'Whatif'];
const SCENARIO_LABELS = { Ideal: 'Ideal', Realistic: 'Realistic', Actual: 'Actual', ML: 'ML', Whatif: 'What-If' };
const GROSS_BREAKDOWN_METRICS = ['Electricity', 'REC', 'Adjustment'];
const TOP_LEVEL_METRICS = ['Gross Revenue', 'Charges & Commission', 'Net Revenue', 'DSM Penalty', 'Net Revenue Post DSM'];

// Per-unit (PU) metrics:
// Electricity is divided by scheduled volume.
// REC, Gross Revenue, Charges & Commission, Net Revenue, DSM Penalty, and Net Revenue Post DSM are divided by downscaled actual generation.
// Adjustment PU follows the exchange formula: ((exchange_gross + (Rec - payable)) / actual_downscaled) - gross_PU,
// where gross_PU = exchange_gross / exchange_cleared volume (Electricity / scheduled).
const SCHEDULE_PU_METRICS = ['Electricity'];
const GEN_PU_METRICS = [
  'REC',
  'Gross Revenue',
  'Charges & Commission',
  'Net Revenue',
  'DSM Penalty',
  'Net Revenue Post DSM'
];
const PU_SUPPORTED_METRICS = [
  ...SCHEDULE_PU_METRICS,
  'Adjustment',
  ...GEN_PU_METRICS
];

const formatPUValue = (val, metric) => {
  if (val == null || isNaN(val) || !isFinite(val)) return '-';
  const num = Number(val);
  const isDeduction = metric === 'Charges & Commission' || metric === 'DSM Penalty';
  const absNum = Math.abs(num);
  let sign = '';
  if (isDeduction) {
    if (absNum > 0.000001) sign = '-';
  } else {
    sign = num < 0 ? '-' : '';
  }
  return `${sign}₹ ${absNum.toFixed(2)}`;
};

const toNum = (v) => (v === undefined || v === null || isNaN(v) ? null : Number(v));

// Extract the table metrics for a single scenario/day from the backend payload.
// Gross Revenue is recomposed (Electricity + REC + signed Adjustment).
// Net Revenue is Gross Revenue - Charges & Commission.
// Net Revenue Post DSM is Net Revenue - DSM Penalty.
const extractScenarioMetrics = (sitData) => {
  if (!sitData) return null;
  const recFromData = toNum(sitData['REC'] !== undefined ? sitData['REC'] : sitData['REC Revenue']);
  const legacyGross = toNum(sitData['Gross Revenue']);
  // Adjustment = Total Receivable - Total Payable (keeps its sign)
  const adjustment = toNum(sitData['Receivable / Payable']) ?? 0;
  const downscaledGen = toNum(
    sitData['Actual Downscaled kWh'] ??
    sitData['actual_gen_downscaled_kwh'] ??
    sitData['downscaled_actual_gen_kwh'] ??
    sitData['Actual Gen kWh'] ??
    sitData['actual_gen_kwh']
  ) ?? 0;
  const gdamKwh = toNum(
    sitData['GDAM kWh'] ??
    sitData['gdam_kwh'] ??
    sitData['G-DAM Volume'] ??
    sitData['GDAM Volume']
  ) ?? 0;
  const rec = recFromData !== null ? recFromData : Math.max(0, (downscaledGen - gdamKwh) * 0.35);
  // Electricity = raw exchange gross revenue (fallback for older payloads: legacy Gross - REC)
  const electricity = toNum(sitData['Electricity']) ?? (legacyGross !== null ? legacyGross - rec : 0);
  const actualScheduledKwh = toNum(
    sitData['Actual Scheduled kWh'] ??
    sitData['actual_scheduled_kwh']
  );
  const whatifScheduledKwh = toNum(
    sitData['Whatif Scheduled kWh'] ??
    sitData['whatif_scheduled_kwh']
  );
  const mlScheduledKwh = toNum(
    sitData['ML Scheduled kWh'] ??
    sitData['ml_scheduled_kwh']
  );
  const idealScheduledKwh = toNum(
    sitData['Ideal Scheduled kWh'] ??
    sitData['ideal_scheduled_kwh']
  );
  const realisticScheduledKwh = toNum(
    sitData['Realistic Scheduled kWh'] ??
    sitData['realistic_scheduled_kwh']
  );
  const scheduledKwh = toNum(
    sitData['Scheduled kWh'] ??
    sitData['scheduled_kwh']
  ) ?? (idealScheduledKwh ?? actualScheduledKwh ?? whatifScheduledKwh ?? mlScheduledKwh ?? 0);

  const grossRevenue = electricity + rec + adjustment;
  const rawCharges = toNum(sitData['Charges & Commission']);
  const chargesAndComm = rawCharges !== null ? rawCharges : null;
  const netRevenue = (grossRevenue !== null && chargesAndComm !== null)
    ? grossRevenue - chargesAndComm
    : toNum(sitData['Net Revenue']);
  const rawDsm = toNum(sitData['DSM Penalty']);
  const dsmPenalty = rawDsm !== null ? rawDsm : null;
  const netRevenuePostDsm = (netRevenue !== null && dsmPenalty !== null)
    ? netRevenue - dsmPenalty
    : toNum(sitData['Net Revenue Post DSM']);

  return {
    'Electricity': electricity,
    'REC': rec,
    'Adjustment': adjustment,
    'Gross Revenue': grossRevenue,
    'Charges & Commission': chargesAndComm,
    'Net Revenue': netRevenue,
    'DSM Penalty': dsmPenalty,
    'Net Revenue Post DSM': netRevenuePostDsm,
    '_downscaled_gen_kwh': downscaledGen,
    '_gdam_kwh': gdamKwh,
    '_scheduled_kwh': scheduledKwh,
    '_actual_scheduled_kwh': actualScheduledKwh,
    '_whatif_scheduled_kwh': whatifScheduledKwh,
    '_ml_scheduled_kwh': mlScheduledKwh,
    '_realistic_scheduled_kwh': realisticScheduledKwh,
    '_ideal_scheduled_kwh': idealScheduledKwh,
  };
};

// Pivot daily_situations into { scenarios, dateKeys, totals, daily, totalsPU, dailyPU, totalDownscaledGen, dailyDownscaledGen, scenarioSchedule, dailySchedule }.
// Totals are the sum across all days in the selected range.
// PU divides Electricity by scheduled volume (Actual sched for Actual, ML sched for ML, What-If sched for What-If),
// and divides REC, Gross Revenue and remaining metrics by downscaled actual generation.
const buildScenarioComparison = (dailySituations) => {
  const allMetrics = [...TOP_LEVEL_METRICS, ...GROSS_BREAKDOWN_METRICS];
  const dateKeys = dailySituations ? Object.keys(dailySituations).sort() : [];
  const present = new Set();
  dateKeys.forEach(dk => Object.keys(dailySituations[dk] || {}).forEach(s => present.add(s)));
  const scenarios = present.size > 0 ? SCENARIO_ORDER.filter(s => present.has(s)) : SCENARIO_ORDER;

  const totals = {};
  const daily = {};
  const totalsPU = {};
  const dailyPU = {};

  scenarios.forEach(s => {
    totals[s] = {};
    totalsPU[s] = {};
    allMetrics.forEach(m => {
      totals[s][m] = null;
      totalsPU[s][m] = null;
    });
  });

  let totalDownscaledGen = 0;
  const dailyDownscaledGen = {};
  const scenarioDownscaledGen = {};
  const scenarioGdamVol = {};
  const scenarioSchedule = {};
  const dailySchedule = {};
  scenarios.forEach(s => {
    scenarioDownscaledGen[s] = 0;
    scenarioGdamVol[s] = 0;
    scenarioSchedule[s] = 0;
  });

  dateKeys.forEach(dk => {
    daily[dk] = {};
    dailyPU[dk] = {};
    dailySchedule[dk] = {};

    const daySit = dailySituations[dk];
    const dayGen = toNum(
      daySit?.Ideal?.['Actual Downscaled kWh'] ??
      daySit?.Actual?.['Actual Downscaled kWh'] ??
      daySit?.Whatif?.['Actual Downscaled kWh'] ??
      daySit?.ML?.['Actual Downscaled kWh'] ??
      daySit?.Actual?.actual_gen_downscaled_kwh
    ) ?? 0;
    dailyDownscaledGen[dk] = dayGen;
    totalDownscaledGen += dayGen;

    // Day's ML scheduled volume (fallback for ML)
    const dayMlSched = toNum(
      daySit?.ML?.['Scheduled kWh'] ??
      daySit?.ML?.['ML Scheduled kWh']
    ) ?? 0;

    scenarios.forEach(s => {
      const m = extractScenarioMetrics(dailySituations[dk]?.[s]);
      daily[dk][s] = m;
      dailyPU[dk][s] = {};

      const sGen = m?._downscaled_gen_kwh > 0 ? m._downscaled_gen_kwh : dayGen;
      scenarioDownscaledGen[s] += sGen;
      scenarioGdamVol[s] += (m?._gdam_kwh ?? 0);

      // PU divisor for Gross Revenue and its constituents:
      // - Ideal uses Ideal scheduled volume (= downscaled actual gen)
      // - Actual uses Actual scheduled volume
      // - ML uses ML scheduled volume
      // - What-If uses What-If scheduled volume
      let sSched = 0;
      if (s === 'Ideal') {
        sSched = m?._ideal_scheduled_kwh ?? m?._scheduled_kwh ?? m?._downscaled_gen_kwh ?? 0;
      } else if (s === 'Actual') {
        sSched = m?._actual_scheduled_kwh ?? m?._scheduled_kwh ?? 0;
      } else if (s === 'ML') {
        sSched = m?._ml_scheduled_kwh ?? (m?._scheduled_kwh > 0 ? m._scheduled_kwh : dayMlSched);
      } else if (s === 'Whatif') {
        sSched = m?._whatif_scheduled_kwh ?? m?._scheduled_kwh ?? 0;
      } else if (s === 'Realistic') {
        sSched = m?._realistic_scheduled_kwh ?? m?._scheduled_kwh ?? 0;
      } else {
        sSched = m?._scheduled_kwh ?? 0;
      }

      dailySchedule[dk][s] = sSched;
      scenarioSchedule[s] += sSched;

      if (!m) {
        allMetrics.forEach(metric => { dailyPU[dk][s][metric] = null; });
        return;
      }

      allMetrics.forEach(metric => {
        if (m[metric] === null) {
          dailyPU[dk][s][metric] = null;
          return;
        }
        totals[s][metric] = (totals[s][metric] ?? 0) + m[metric];

        if (metric === 'Adjustment') {
          // (exchange_gross + (Rec - payable) / actual_downscaled) - gross_PU where gross_PU = exchange_gross / exchange_cleared volume
          const exchGross = m['Electricity'] ?? 0;
          const recMinusPayable = m['Adjustment'] ?? 0;
          const grossPU = sSched > 0 ? exchGross / sSched : 0;
          dailyPU[dk][s][metric] = sGen > 0
            ? ((exchGross + recMinusPayable) / sGen) - grossPU
            : null;
        } else if (SCHEDULE_PU_METRICS.includes(metric)) {
          dailyPU[dk][s][metric] = sSched > 0 ? (m[metric] / sSched) : null;
        } else if (GEN_PU_METRICS.includes(metric)) {
          dailyPU[dk][s][metric] = sGen > 0 ? (m[metric] / sGen) : null;
        } else {
          dailyPU[dk][s][metric] = null;
        }
      });
    });
  });

  // Re-calculate period-level REC and downstream revenues using the aggregation-first approach:
  // REC = (SUM(Actual Generation) - SUM(G-DAM Volume)) * 0.35
  // For a single selected date, SUM(Actual Generation) and SUM(G-DAM Volume) are that date's values.
  // For a multi-day date range, period totals are used once, rather than summing daily clamped RECs.
  // Then flow the period REC into Gross Revenue, Net Revenue, and Net Revenue Post DSM.
  if (dateKeys.length > 0) {
    scenarios.forEach(s => {
      const hasScenarioData = dateKeys.some(dk => Boolean(daily[dk]?.[s]));
      if (!hasScenarioData) return;

      const sumActualGen = scenarioDownscaledGen[s] > 0 ? scenarioDownscaledGen[s] : totalDownscaledGen;
      const sumGdamVol = scenarioGdamVol[s] ?? 0;
      const periodRec = Math.max(0, (sumActualGen - sumGdamVol) * 0.35);

      totals[s]['REC'] = periodRec;

      const totElec = totals[s]?.['Electricity'] ?? 0;
      const totAdj = totals[s]?.['Adjustment'] ?? 0;
      const rawCharges = totals[s]?.['Charges & Commission'];
      const rawDsm = totals[s]?.['DSM Penalty'];
      const totCharges = rawCharges !== null && rawCharges !== undefined ? rawCharges : 0;
      const totDsm = rawDsm !== null && rawDsm !== undefined ? rawDsm : 0;

      const totGross = totElec + periodRec + totAdj;
      const totNet = rawCharges !== null && rawCharges !== undefined ? totGross - totCharges : totGross;
      const totPostDsm = rawDsm !== null && rawDsm !== undefined ? totNet - totDsm : totNet;

      totals[s]['Gross Revenue'] = totGross;
      totals[s]['Net Revenue'] = totNet;
      totals[s]['Net Revenue Post DSM'] = totPostDsm;
    });
  }

  scenarios.forEach(s => {
    const sGen = scenarioDownscaledGen[s] > 0 ? scenarioDownscaledGen[s] : totalDownscaledGen;
    const sSched = scenarioSchedule[s];

    allMetrics.forEach(metric => {
      const raw = totals[s]?.[metric];
      if (raw === null || raw === undefined) {
        totalsPU[s][metric] = null;
        return;
      }

      if (metric === 'Adjustment') {
        const exchGross = totals[s]?.['Electricity'] ?? 0;
        const recMinusPayable = totals[s]?.['Adjustment'] ?? 0;
        const grossPU = sSched > 0 ? exchGross / sSched : 0;
        totalsPU[s][metric] = sGen > 0
          ? ((exchGross + recMinusPayable) / sGen) - grossPU
          : null;
      } else if (SCHEDULE_PU_METRICS.includes(metric)) {
        totalsPU[s][metric] = sSched > 0 ? (raw / sSched) : null;
      } else if (GEN_PU_METRICS.includes(metric)) {
        totalsPU[s][metric] = sGen > 0 ? (raw / sGen) : null;
      } else {
        totalsPU[s][metric] = null;
      }
    });
  });

  return {
    scenarios,
    dateKeys,
    totals,
    daily,
    totalsPU,
    dailyPU,
    totalDownscaledGen,
    dailyDownscaledGen,
    scenarioSchedule,
    dailySchedule,
  };
};

export default function WhatIfPage() {
  const { user } = useAuth();
  const storedUser = React.useMemo(() => {
    try {
      const cached = localStorage.getItem('user_info');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  }, []);
  const canEditBidding = Boolean(
    user?.is_superuser ||
    storedUser?.is_superuser ||
    user?.admin_apps?.includes('trading') ||
    storedUser?.admin_apps?.includes('trading')
  );

  const [activeTab, setActiveTab] = useState('day_ahead');

  // --- Plant States (Used globally) ---
  const [plants, setPlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(null);

  // --- TAB 1: Day-Ahead states & handlers ---
  const [selectedDate, setSelectedDate] = useState(
    IS_DAY_AHEAD_READY ? dayjs().add(1, 'day') : dayjs('2026-06-20')
  );
  const [blocksData, setBlocksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOverride, setIsOverride] = useState(false);
  const [allocationMode, setAllocationMode] = useState('rules'); // 'rules' | 'ml'
  const [autoSubmitMode, setAutoSubmitMode] = useState('rules'); // 'rules' | 'ml'
  const [isBefore10AmFallback, setIsBefore10AmFallback] = useState(false);
  const [fallbackDate, setFallbackDate] = useState(null);
  const [mlCustomPrices, setMlCustomPrices] = useState({});
  const [daAllocationCap, setDaAllocationCap] = useState(null);
  const [isSavingDaCap, setIsSavingDaCap] = useState(false);

  const [currentTime, setCurrentTime] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // Submit button is only enabled between 9:00 AM and 10:45 AM
  const isSubmitWindowOpen = React.useMemo(() => {
    const currentMinutes = currentTime.hour() * 60 + currentTime.minute();
    const startMinutes = 9 * 60; // 9:00 AM = 540
    const endMinutes = 10 * 60 + 45; // 10:45 AM = 645
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }, [currentTime]);

  const isReadOnlyMode = !canEditBidding || isBefore10AmFallback;

  // Get current plant's submission flags
  const currentPlantData = React.useMemo(() => {
    if (!selectedPlant || plants.length === 0) return null;
    return plants.find(p => p.id === selectedPlant) || null;
  }, [selectedPlant, plants]);

  const effectiveDaCap = daAllocationCap != null
    ? daAllocationCap
    : (currentPlantData?.max_allocation_allowed != null ? Number(currentPlantData.max_allocation_allowed) : 100);

  useEffect(() => {
    if (currentPlantData && currentPlantData.max_allocation_allowed != null) {
      setDaAllocationCap(Number(currentPlantData.max_allocation_allowed));
    }
  }, [selectedPlant, currentPlantData?.max_allocation_allowed]);

  const isManualSubmissionAllowed = currentPlantData?.allow_manual_submission !== false;
  const isAutoSubmissionAllowed = currentPlantData?.allow_auto_submission !== false;

  const isSubmitDisabled = isReadOnlyMode || !isSubmitWindowOpen || !isManualSubmissionAllowed;

  // Override configuration rules states
  const [savedRules, setSavedRules] = useState([]);
  const [modalRules, setModalRules] = useState([]);
  const [modalActiveTab, setModalActiveTab] = useState('allocation');

  // Check whether modalRules cover all 96 blocks (last rule end_block is 96 and starts at 1)
  const isAllBlocksCovered = React.useMemo(() => {
    if (!modalRules || modalRules.length === 0) return false;
    const lastRule = modalRules[modalRules.length - 1];
    return lastRule && lastRule.end_block === 96;
  }, [modalRules]);

  // Check whether modalRules differ from savedRules (initial state)
  const isRulesChanged = React.useMemo(() => {
    if (!savedRules && !modalRules) return false;
    if (!savedRules || !modalRules) return true;
    if (savedRules.length !== modalRules.length) return true;

    for (let i = 0; i < modalRules.length; i++) {
      const m = modalRules[i];
      const s = savedRules[i];
      if (!s) return true;
      if (
        m.start_block !== s.start_block ||
        m.end_block !== s.end_block ||
        m.allocation_pct !== s.allocation_pct ||
        m.market !== s.market ||
        Number(m.bid_price) !== Number(s.bid_price)
      ) {
        return true;
      }
    }
    return false;
  }, [savedRules, modalRules]);

  // Save Allocation is enabled only when rules have changed, covers all 96 blocks, and not read-only
  const isSaveAllocationDisabled = !isAllBlocksCovered || !isRulesChanged || isReadOnlyMode;

  // Bid submission audit log state for top banner
  const [latestSubmissionLog, setLatestSubmissionLog] = useState(null);

  const fetchLatestSubmissionLog = async (plantId, dateVal) => {
    if (!plantId) return;
    try {
      const targetDate = dateVal || selectedDate;
      const formattedDate = targetDate ? targetDate.format('YYYY-MM-DD') : null;
      const res = await getBidSubmissionHistory({
        plant_id: plantId,
        ...(formattedDate ? { delivery_date: formattedDate } : {})
      });
      const results = res.data?.results || [];
      if (results.length > 0) {
        setLatestSubmissionLog(results[0]);
      } else {
        setLatestSubmissionLog(null);
      }
    } catch (err) {
      console.error('Failed to fetch latest bid submission log:', err);
      setLatestSubmissionLog(null);
    }
  };

  const handleDownloadSubmissionExcel = (logItem) => {
    const log = logItem || latestSubmissionLog;
    if (!log) {
      message.info('No bid submission details found to download.');
      return;
    }

    const gdamBids = Array.isArray(log.gdam_bids) ? log.gdam_bids : [];
    const damBids = Array.isArray(log.dam_bids) ? log.dam_bids : [];

    const rows = [];
    gdamBids.forEach(item => {
      rows.push({
        'Block': item.block != null ? item.block : '',
        'Segment': 'GDAM',
        'Value (MW)': item.quantity != null ? Number(item.quantity) : 0,
        'Price (₹/MWh)': item.rate != null ? Number(item.rate) : (item.bid_price != null ? Number(item.bid_price) : 0)
      });
    });

    damBids.forEach(item => {
      rows.push({
        'Block': item.block != null ? item.block : '',
        'Segment': 'DAM',
        'Value (MW)': item.quantity != null ? Number(item.quantity) : 0,
        'Price (₹/MWh)': item.rate != null ? Number(item.rate) : (item.bid_price != null ? Number(item.bid_price) : 0)
      });
    });

    // Sort rows by Block ascending, then Segment
    rows.sort((a, b) => {
      if (a['Block'] !== b['Block']) return Number(a['Block']) - Number(b['Block']);
      return a['Segment'].localeCompare(b['Segment']);
    });

    if (rows.length === 0) {
      message.warning('No bid detail rows found in this submission log.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bid Details');

    const filename = `bid_submission_${log.plant_name || log.plant_id || 'plant'}_${log.delivery_date || 'date'}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // OCF states
  const [ocf, setOcf] = useState('Yes');
  const [ocfOption, setOcfOption] = useState('PREMIUM');
  const [ocfValue, setOcfValue] = useState(0);

  const fetchOverrideRules = async (plantId) => {
    if (!plantId) return;
    try {
      const res = await getGDAMOverrideRules({ plant_id: plantId });
      const resBid = await getGDAMBidPriceOverrideRules({ plant_id: plantId });

      const allocRules = res.data && res.data.rules ? res.data.rules : [];
      const bidRules = resBid.data && resBid.data.rules ? resBid.data.rules : [];

      const currentPlant = plants.find(p => p.id === plantId);
      const plantGdamPrice = currentPlant?.gdam_bid_price ?? 550;
      const plantDamPrice = currentPlant?.dam_bid_price ?? 550;

      const combinedRules = allocRules.map((r, idx) => {
        const bp = bidRules.find(b => b.start_block === r.start_block && b.end_block === r.end_block);
        let price = 550;
        if (bp) {
          price = r.market === 'GDAM' ? bp.gdam_bid_price : bp.dam_bid_price;
        } else {
          price = r.market === 'GDAM' ? plantGdamPrice : plantDamPrice;
        }
        return {
          key: r.id || idx,
          start_block: Math.min(96, Math.max(1, r.start_block)),
          end_block: Math.min(96, Math.max(1, r.end_block)),
          allocation_pct: r.allocation_pct,
          market: r.market,
          bid_price: price
        };
      });

      setSavedRules(combinedRules);
      setModalRules(combinedRules);
      setIsOverride(combinedRules.length > 0);
    } catch (err) {
      console.error('Error fetching override rules:', err);
    }
  };

  const handleAllocationModeChange = async (newMode) => {
    if (!newMode || newMode === allocationMode) return;
    setAllocationMode(newMode);
    if (!selectedPlant) return;

    // Optimistically update plants array in state
    setPlants(prev => prev.map(p => p.id === selectedPlant ? { ...p, allocation_mode: newMode } : p));

    try {
      await updatePlant(selectedPlant, { allocation_mode: newMode });
      message.success(`Allocation method set to ${newMode === 'rules' ? 'Manual Allocation' : 'ML Allocation'}`);
    } catch (err) {
      console.error('Failed to persist allocation mode:', err);
      message.error('Failed to save allocation method to database.');
    }
  };

  const handleAutoSubmitModeChange = async (newMode) => {
    if (!newMode || newMode === autoSubmitMode) return;
    setAutoSubmitMode(newMode);
    if (!selectedPlant) return;

    // Optimistically update plants array in state
    setPlants(prev => prev.map(p => p.id === selectedPlant ? { ...p, auto_submit_mode: newMode } : p));

    try {
      await updatePlant(selectedPlant, { auto_submit_mode: newMode });
      message.success(`Auto submit mode set to ${newMode === 'rules' ? 'Manual Allocation' : 'ML Allocation'}`);
    } catch (err) {
      console.error('Failed to persist auto submit mode:', err);
      message.error('Failed to save auto submit mode to database.');
    }
  };

  const handleSaveDaAllocationCap = async (customVal) => {
    if (isReadOnlyMode) {
      message.warning('Read-Only Mode: Updating plant configuration is disabled.');
      return;
    }
    const valToSave = customVal !== undefined ? customVal : effectiveDaCap;
    if (valToSave == null || isNaN(valToSave)) {
      message.error('Please enter a valid percentage.');
      return;
    }
    const clampedVal = Math.min(100, Math.max(0, Number(valToSave)));
    setDaAllocationCap(clampedVal);
    if (!selectedPlant) return;

    setIsSavingDaCap(true);
    // Optimistically update plants array in state
    setPlants(prev => prev.map(p => p.id === selectedPlant ? { ...p, max_allocation_allowed: clampedVal } : p));

    try {
      await updatePlant(selectedPlant, { max_allocation_allowed: clampedVal });
      message.success(`% of Morning Forecast to be Allocated in DA Market updated to ${clampedVal}%`);
      fetchData();
    } catch (err) {
      console.error('Failed to persist max_allocation_allowed:', err);
      message.error('Failed to save DA Market Allocation % to plant master.');
    } finally {
      setIsSavingDaCap(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedPlant) {
        params.plant_id = selectedPlant;
        params.plant = selectedPlant;
      }
      params.date = selectedDate.format('YYYY-MM-DD');

      const response = await getMLGDAMAllocation(params);
      const data = response.data;
      if (data && data.allocation_mode) {
        setAllocationMode(data.allocation_mode);
      }
      if (data && data.auto_submit_mode) {
        setAutoSubmitMode(data.auto_submit_mode);
      }
      if (data && data.max_allocation_allowed !== undefined && data.max_allocation_allowed !== null) {
        setDaAllocationCap(Number(data.max_allocation_allowed));
      }
      if (data && data.results && data.results.length > 0) {
        setBlocksData(data.results);
        setIsBefore10AmFallback(false);
        setFallbackDate(null);
        const queryDate = data.date ? dayjs(data.date) : selectedDate;
        if (!selectedDate || !queryDate.isSame(selectedDate, 'day')) {
          setSelectedDate(queryDate);
        }

        // Also fetch saved rules for the plant
        const rulesPlant = data.plant_id || selectedPlant;
        if (rulesPlant) {
          await fetchOverrideRules(rulesPlant);
        }
      } else {
        // No data found for requested date (e.g. before 10 AM) -> fallback to previous day
        const prevDate = selectedDate.subtract(1, 'day');
        const prevParams = { ...params, date: prevDate.format('YYYY-MM-DD') };
        try {
          const prevResponse = await getMLGDAMAllocation(prevParams);
          const prevData = prevResponse.data;
          if (prevData && prevData.results && prevData.results.length > 0) {
            setBlocksData(prevData.results);
            setIsBefore10AmFallback(true);
            setFallbackDate(prevDate);
            if (prevData && prevData.max_allocation_allowed !== undefined && prevData.max_allocation_allowed !== null) {
              setDaAllocationCap(Number(prevData.max_allocation_allowed));
            }
            const rulesPlant = prevData.plant_id || selectedPlant;
            if (rulesPlant) {
              await fetchOverrideRules(rulesPlant);
            }
          } else {
            setBlocksData([]);
            setIsBefore10AmFallback(false);
            setFallbackDate(null);
          }
        } catch (prevErr) {
          console.error('Error fetching fallback data for previous day:', prevErr);
          setBlocksData([]);
          setIsBefore10AmFallback(false);
          setFallbackDate(null);
        }
      }
    } catch (error) {
      console.error('Error fetching ML GDAM Allocation data:', error);
      const prevDate = selectedDate.subtract(1, 'day');
      try {
        const prevParams = { plant_id: selectedPlant, plant: selectedPlant, date: prevDate.format('YYYY-MM-DD') };
        const prevResponse = await getMLGDAMAllocation(prevParams);
        const prevData = prevResponse.data;
        if (prevData && prevData.results && prevData.results.length > 0) {
          setBlocksData(prevData.results);
          setIsBefore10AmFallback(true);
          setFallbackDate(prevDate);
          const rulesPlant = prevData.plant_id || selectedPlant;
          if (rulesPlant) {
            await fetchOverrideRules(rulesPlant);
          }
        } else {
          setBlocksData([]);
          setIsBefore10AmFallback(false);
          setFallbackDate(null);
        }
      } catch (e) {
        setBlocksData([]);
        setIsBefore10AmFallback(false);
        setFallbackDate(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'day_ahead') {
      fetchData();
      if (selectedPlant) {
        fetchLatestSubmissionLog(selectedPlant, selectedDate);
      }
    }
  }, [activeTab, selectedPlant, selectedDate]);

  const handleOpenOverrideModal = () => {
    // Clone savedRules to modalRules
    setModalRules(
      savedRules.map((r, idx) => ({
        key: r.id || idx,
        start_block: Math.min(96, Math.max(1, r.start_block)),
        end_block: Math.min(96, Math.max(1, r.end_block)),
        allocation_pct: r.allocation_pct,
        market: r.market
      }))
    );

    // Clone savedBidPriceRules to modalBidPriceRules
    setModalBidPriceRules(
      savedBidPriceRules.map((r, idx) => ({
        key: r.id || idx,
        start_block: Math.min(96, Math.max(1, r.start_block)),
        end_block: Math.min(96, Math.max(1, r.end_block)),
        gdam_bid_price: r.gdam_bid_price,
        dam_bid_price: r.dam_bid_price
      }))
    );

    // Find bid prices from plants array
    const currentPlant = plants.find(p => p.id === selectedPlant);
    if (currentPlant) {
      setModalGdamBidPrice(currentPlant.gdam_bid_price != null ? currentPlant.gdam_bid_price : 551);
      setModalDamBidPrice(currentPlant.dam_bid_price != null ? currentPlant.dam_bid_price : 551);
    } else {
      setModalGdamBidPrice(551);
      setModalDamBidPrice(551);
    }

    setModalActiveTab('allocation');
    setIsModalVisible(true);
  };

  const handleAddModalRule = () => {
    if (isReadOnlyMode) {
      message.warning('Read-Only Mode: Adding bidding rules is disabled.');
      return;
    }
    const currentPlant = plants.find(p => p.id === selectedPlant);
    const defaultPrice = currentPlant?.gdam_bid_price ?? 550;

    if (modalRules.length === 0) {
      setModalRules([
        {
          key: 1,
          start_block: 1,
          end_block: 96,
          allocation_pct: 0,
          market: 'GDAM',
          bid_price: defaultPrice
        }
      ]);
      return;
    }
    const lastRule = modalRules[modalRules.length - 1];
    if (lastRule.end_block >= 96) return;

    const nextKey = Math.max(...modalRules.map(r => r.key)) + 1;
    const nextStart = lastRule.end_block + 1;
    setModalRules([
      ...modalRules,
      {
        key: nextKey,
        start_block: nextStart,
        end_block: 96,
        allocation_pct: 0,
        market: 'GDAM',
        bid_price: lastRule.bid_price ?? defaultPrice
      }
    ]);
  };

  const handleUpdateModalRule = (key, field, value) => {
    if (isReadOnlyMode) return;
    setModalRules(prev => {
      const idx = prev.findIndex(r => r.key === key);
      if (idx === -1) return prev;

      const updated = prev.map(r => ({ ...r }));
      updated[idx][field] = value;

      if (field === 'end_block') {
        if (updated[idx].end_block < updated[idx].start_block) {
          updated[idx].end_block = updated[idx].start_block;
        }
        if (updated[idx].end_block > 96) {
          updated[idx].end_block = 96;
        }

        for (let i = idx + 1; i < updated.length; i++) {
          updated[i].start_block = updated[i - 1].end_block + 1;
          if (updated[i].end_block < updated[i].start_block) {
            updated[i].end_block = Math.min(96, updated[i].start_block);
          }
        }
      }

      return updated.filter(r => r.start_block <= 96);
    });
  };

  const handleDeleteModalRule = (key) => {
    if (isReadOnlyMode) return;
    setModalRules(prev => {
      const filtered = prev.filter(r => r.key !== key);
      const updated = filtered.map(r => ({ ...r }));

      for (let i = 0; i < updated.length; i++) {
        if (i === 0) {
          updated[i].start_block = 1;
        } else {
          updated[i].start_block = updated[i - 1].end_block + 1;
        }

        if (updated[i].end_block < updated[i].start_block) {
          updated[i].end_block = Math.min(96, updated[i].start_block);
        }
      }

      return updated.filter(r => r.start_block <= 96);
    });
  };

  const validateRules = (rules) => {
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (r.start_block == null || r.end_block == null || r.allocation_pct == null || !r.market || r.bid_price == null) {
        return 'All fields (Start Block, End Block, Allocation %, Market Selection, Price) are required for every rule.';
      }
      if (r.start_block < 1 || r.start_block > 96 || r.end_block < 1 || r.end_block > 96) {
        return 'Block ranges must be between 1 and 96.';
      }
      if (r.start_block > r.end_block) {
        return `Rule ${i + 1}: Start block (${r.start_block}) cannot be greater than End block (${r.end_block}).`;
      }
      if (r.allocation_pct < 0 || r.allocation_pct > 100) {
        return 'Allocation percentage must be between 0 and 100.';
      }
      if (r.bid_price < 0) {
        return 'Price must be greater than or equal to 0.';
      }
    }

    // Overlap validation
    const coveredBlocks = new Array(97).fill(false);
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      for (let b = r.start_block; b <= r.end_block; b++) {
        if (coveredBlocks[b]) {
          return `Overlap detected on Block ${b}. Please ensure block ranges do not overlap.`;
        }
        coveredBlocks[b] = true;
      }
    }
    return null;
  };

  const handleSaveOverrideRules = async () => {
    if (isReadOnlyMode) {
      message.warning('Read-Only Mode: Modifying allocation rules is disabled.');
      return;
    }
    const errorMsg = validateRules(modalRules);
    if (errorMsg) {
      message.error(errorMsg);
      return;
    }

    setLoading(true);
    try {
      const currentPlant = plants.find(p => p.id === selectedPlant);
      const plantGdamPrice = currentPlant?.gdam_bid_price ?? 550;
      const plantDamPrice = currentPlant?.dam_bid_price ?? 550;

      const allocationPayload = {
        plant_id: selectedPlant || 1,
        rules: modalRules.map(r => ({
          start_block: r.start_block,
          end_block: r.end_block,
          allocation_pct: r.allocation_pct,
          market: r.market
        }))
      };

      const bidPricePayload = {
        plant_id: selectedPlant || 1,
        rules: modalRules.map(r => ({
          start_block: r.start_block,
          end_block: r.end_block,
          gdam_bid_price: r.market === 'GDAM' ? (r.bid_price ?? plantGdamPrice) : plantGdamPrice,
          dam_bid_price: r.market === 'DAM' ? (r.bid_price ?? plantDamPrice) : plantDamPrice
        }))
      };

      const res1 = await saveGDAMOverrideRules(allocationPayload);
      const res2 = await saveGDAMBidPriceOverrideRules(bidPricePayload);

      if (res1.data?.status === 'success' && res2.data?.status === 'success') {
        message.success('Override rules saved successfully!');
        setSavedRules(modalRules);
        setIsOverride(modalRules.length > 0);
      } else {
        message.error('Failed to save override rules.');
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || 'Failed to save override rules.');
    } finally {
      setLoading(false);
    }
  };

  const getBidPricesForBlock = (blockNumber) => {
    const currentPlant = plants.find(p => p.id === selectedPlant);
    const activeRules = modalRules.length > 0 ? modalRules : savedRules;
    if (activeRules.length > 0) {
      const rule = activeRules.find(r => blockNumber >= r.start_block && blockNumber <= r.end_block);
      if (rule) {
        return {
          gdam: rule.market === 'GDAM' ? rule.bid_price : (currentPlant?.gdam_bid_price ?? 551),
          dam: rule.market === 'DAM' ? rule.bid_price : (currentPlant?.dam_bid_price ?? 551)
        };
      }
    }
    return {
      gdam: currentPlant?.gdam_bid_price ?? 551,
      dam: currentPlant?.dam_bid_price ?? 551
    };
  };

  const getMlBidPricesForBlock = (blockNumber) => {
    const currentPlant = plants.find(p => p.id === selectedPlant);
    const defaultGdam = currentPlant?.gdam_bid_price ?? 551;
    const defaultDam = currentPlant?.dam_bid_price ?? 551;

    const matchedRangeKey = Object.keys(mlCustomPrices).find(startKey => {
      const entry = mlCustomPrices[startKey];
      if (entry && entry.end_block) {
        return blockNumber >= Number(startKey) && blockNumber <= entry.end_block;
      }
      return blockNumber === Number(startKey);
    });

    if (matchedRangeKey !== undefined) {
      const entry = mlCustomPrices[matchedRangeKey];
      const val = typeof entry === 'object' ? entry.price : entry;
      if (val !== undefined && val !== null && !isNaN(val)) {
        return {
          gdam: Number(val),
          dam: Number(val)
        };
      }
    }

    return {
      gdam: defaultGdam,
      dam: defaultDam
    };
  };

  const getRuleBasedAllocatedVolumes = (blockNumber, morningForecast, defaultGdam) => {
    const activeRules = modalRules.length > 0 ? modalRules : savedRules;
    if (activeRules.length === 0) {
      return {
        gdam: defaultGdam,
        dam: 0
      };
    }
    const rule = activeRules.find(r => blockNumber >= r.start_block && blockNumber <= r.end_block);
    if (!rule) {
      return {
        gdam: 0,
        dam: 0
      };
    }
    const allocatedVol = morningForecast * (Number(rule.allocation_pct || 0) / 100);
    if (rule.market === 'GDAM') {
      return {
        gdam: allocatedVol,
        dam: 0
      };
    } else if (rule.market === 'DAM') {
      return {
        gdam: 0,
        dam: allocatedVol
      };
    }
    return {
      gdam: 0,
      dam: 0
    };
  };

  const getAllocatedVolumes = (blockNumber, morningForecast, defaultGdam, blockObj = null) => {
    if (allocationMode === 'ml') {
      const predRatio = blockObj?.predicted_ratio !== undefined && blockObj?.predicted_ratio !== null
        ? Number(blockObj.predicted_ratio)
        : null;
      const rtmWins = predRatio !== null ? predRatio > 1.0 : defaultGdam === 0;
      const daCapRatio = Math.min(100, Math.max(0, effectiveDaCap)) / 100;
      return {
        gdam: rtmWins ? 0 : (morningForecast * daCapRatio),
        dam: 0
      };
    }
    const activeRules = modalRules.length > 0 ? modalRules : savedRules;
    if (activeRules.length === 0) {
      return {
        gdam: defaultGdam,
        dam: 0
      };
    }
    const rule = activeRules.find(r => blockNumber >= r.start_block && blockNumber <= r.end_block);
    if (!rule) {
      return {
        gdam: 0,
        dam: 0
      };
    }
    const allocatedVol = morningForecast * (Number(rule.allocation_pct || 0) / 100);
    if (rule.market === 'GDAM') {
      return {
        gdam: allocatedVol,
        dam: 0
      };
    } else if (rule.market === 'DAM') {
      return {
        gdam: 0,
        dam: allocatedVol
      };
    }
    return {
      gdam: 0,
      dam: 0
    };
  };

  const handleFinalSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isReadOnlyMode) {
      message.warning('Read-Only Mode: Submitting bids is disabled.');
      return;
    }

    if (!isManualSubmissionAllowed) {
      message.warning('Manual submission is disabled for this plant.');
      return;
    }

    if (!isSubmitWindowOpen) {
      message.warning('Bid submission is only allowed between 9:00 AM and 10:45 AM.');
      return;
    }

    if (allocationMode === 'rules') {
      const activeRules = modalRules.length > 0 ? modalRules : savedRules;
      if (!activeRules || activeRules.length === 0) {
        message.error('total number of blocks should be 96');
        return;
      }
      const lastRule = activeRules[activeRules.length - 1];
      if (!lastRule || Number(lastRule.end_block) !== 96) {
        message.error('total number of blocks should be 96');
        return;
      }
    }

    const currentPlant = plants.find(p => p.id === selectedPlant);
    const gdam = [];
    const dam = [];

    blocksData.forEach(block => {
      const f1Val = Number(block.gdam_vol_allocation_f1 || 0);
      const morningForecastVal = Number(block.morning_forecast_after_rtc || 0);
      const vols = getAllocatedVolumes(block.block, morningForecastVal, f1Val, block);
      const bidPrices = allocationMode === 'ml'
        ? getMlBidPricesForBlock(block.block)
        : getBidPricesForBlock(block.block);

      if (vols.gdam > 0) {
        gdam.push({
          block: block.block,
          rate: bidPrices.gdam,
          quantity: Number(vols.gdam.toFixed(1))
        });
      }

      if (vols.dam > 0) {
        dam.push({
          block: block.block,
          rate: bidPrices.dam,
          quantity: Number(vols.dam.toFixed(1))
        });
      }
    });

    if (gdam.length === 0 && dam.length === 0) {
      message.error('No positive volumes to submit for GDAM or DAM.');
      return;
    }

    const deliveryDateStr = selectedDate ? selectedDate.format('DD-MM-YYYY') : dayjs().add(1, 'day').format('DD-MM-YYYY');
    const plantName = currentPlant?.name || `Plant ${selectedPlant}`;
    const modeLabel = allocationMode === 'rules' ? 'Manual Allocation' : 'ML Allocation';

    const payload = {
      plant_id: selectedPlant,
      delivery_date: deliveryDateStr,
      allocation_mode: allocationMode,
      ocf,
      ocfOption,
      ocfValue,
      gdam,
      dam
    };

    Modal.confirm({
      title: 'Confirm Bid Submission',
      content: (
        <div style={{ marginTop: '8px', fontSize: '13px', color: '#475569' }}>
          <p style={{ margin: '4px 0' }}>Are you sure you want to submit the scenario bids?</p>
          <ul style={{ paddingLeft: '18px', margin: '8px 0 0 0' }}>
            <li><strong>Delivery Date:</strong> {deliveryDateStr}</li>
            <li><strong>Method:</strong> {modeLabel}</li>
            <li><strong>GDAM Blocks:</strong> {gdam.length}</li>
            <li><strong>DAM Blocks:</strong> {dam.length}</li>
          </ul>
        </div>
      ),
      okText: 'Yes, Submit',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          background: '#0F172A',
          borderColor: '#0F172A',
          fontWeight: 600
        }
      },
      onOk: async () => {
        setLoading(true);
        try {
          console.log('Submitting scenario bids payload:', payload);
          const res = await submitScenarioBids(payload);
          message.success(res.data?.message || 'Bids submitted and logged successfully!');
          if (res.data?.submission_log) {
            setLatestSubmissionLog(res.data.submission_log);
          } else if (selectedPlant) {
            fetchLatestSubmissionLog(selectedPlant, selectedDate);
          }
        } catch (err) {
          console.error('Failed to submit scenario bids:', err);
          message.error(err.response?.data?.error || 'Failed to submit bids to client API.');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Pre-calculate Tab 1 metrics
  let totalMorningForecast = 0;
  let totalGDAMAllocated = 0;
  let totalDAMAllocated = 0;

  let totalGDAMRuleAllocated = 0;
  let totalDAMRuleAllocated = 0;
  let ruleGdamBlocks = 0;
  let ruleDamBlocks = 0;
  let ruleRtmBlocks = 0;

  let totalMlGdamVol = 0;
  let totalMlDamVol = 0;
  let totalMlRtmVol = 0;
  let mlGdamBlocks = 0;
  let mlDamBlocks = 0;
  let mlRtmBlocks = 0;

  const dataRows = blocksData.map(block => {
    const f1Val = Number(block.gdam_vol_allocation_f1 || 0);
    const morningForecastVal = Number(block.morning_forecast_after_rtc || 0);

    // Rule-based volumes
    const volsRule = getRuleBasedAllocatedVolumes(block.block, morningForecastVal, f1Val);
    const ruleGdamVol = volsRule.gdam;
    const ruleDamVol = volsRule.dam;
    const ruleRtmVol = Math.max(0, morningForecastVal - ruleGdamVol - ruleDamVol);

    if (ruleGdamVol > 0) {
      ruleGdamBlocks += 1;
    } else if (ruleDamVol > 0) {
      ruleDamBlocks += 1;
    } else {
      ruleRtmBlocks += 1;
    }

    totalGDAMRuleAllocated += ruleGdamVol;
    totalDAMRuleAllocated += ruleDamVol;

    // ML Calculation Logic per user spec:
    // 1. DAM: Blocks = 0, Volume = 0
    // 2. GDAM vs RTM Winner: RTM wins if predicted_ratio > 1.0 (or GDAM wins if predicted_ratio <= 1.0)
    // 3. If GDAM wins: GDAM gets f1Val, RTM Volume = Morning Forecast - GDAM Volume, count in mlGdamBlocks
    // 4. If RTM wins: GDAM = 0, RTM Volume = Morning Forecast, count in mlRtmBlocks (only where RTM won)
    const predRatio = block.predicted_ratio !== undefined && block.predicted_ratio !== null
      ? Number(block.predicted_ratio)
      : null;
    const rtmWins = predRatio !== null ? predRatio > 1.0 : f1Val === 0;
    const gdamWins = !rtmWins;

    const daCapRatio = Math.min(100, Math.max(0, effectiveDaCap)) / 100;
    const mlGdamVol = gdamWins ? (morningForecastVal * daCapRatio) : 0;
    const mlRtmVol = Math.max(0, morningForecastVal - mlGdamVol);
    const mlDamVol = 0;

    if (gdamWins && mlGdamVol > 0) {
      mlGdamBlocks += 1;
    } else {
      mlRtmBlocks += 1;
    }

    totalMlGdamVol += mlGdamVol;
    totalMlRtmVol += mlRtmVol;

    // Selected active volumes (based on toggle)
    const vols = getAllocatedVolumes(block.block, morningForecastVal, f1Val, block);
    const bidPrices = allocationMode === 'ml'
      ? getMlBidPricesForBlock(block.block)
      : getBidPricesForBlock(block.block);
    const rtmVol = Math.max(0, morningForecastVal - vols.gdam - vols.dam);

    totalMorningForecast += morningForecastVal;
    totalGDAMAllocated += vols.gdam;
    totalDAMAllocated += vols.dam;

    return {
      key: block.block,
      block_number: block.block,
      time_block: block.time_block,
      morning_forecast_after_rtc: morningForecastVal,

      gdam_vol_allocated: vols.gdam,
      dam_vol_allocated: vols.dam,
      rtm_vol_allocated: rtmVol,

      rule_gdam_vol: ruleGdamVol,
      rule_dam_vol: ruleDamVol,
      rule_rtm_vol: ruleRtmVol,

      ml_gdam_vol: mlGdamVol,
      ml_dam_vol: mlDamVol,
      ml_rtm_vol: mlRtmVol,

      gdam_bid_price: vols.gdam > 0 ? bidPrices.gdam : 0,
      dam_bid_price: vols.dam > 0 ? bidPrices.dam : 0
    };
  });

  const avgMorningForecast = blocksData.length > 0 ? (totalMorningForecast / blocksData.length) : 0;
  const avgGDAMAllocated = blocksData.length > 0 ? (totalGDAMAllocated / blocksData.length) : 0;
  const avgDAMAllocated = blocksData.length > 0 ? (totalDAMAllocated / blocksData.length) : 0;
  const avgRTMAllocated = Math.max(0, avgMorningForecast - avgGDAMAllocated - avgDAMAllocated);

  const avgGDAMRuleAllocated = blocksData.length > 0 ? (totalGDAMRuleAllocated / blocksData.length) : 0;
  const avgDAMRuleAllocated = blocksData.length > 0 ? (totalDAMRuleAllocated / blocksData.length) : 0;
  const avgRTMRuleAllocated = Math.max(0, avgMorningForecast - avgGDAMRuleAllocated - avgDAMRuleAllocated);

  const totalRuleAllocatedAvg = avgGDAMRuleAllocated + avgDAMRuleAllocated + avgRTMRuleAllocated;
  const gdamRuleSharePct = totalRuleAllocatedAvg > 0 ? (avgGDAMRuleAllocated / totalRuleAllocatedAvg) * 100 : 0;
  const damRuleSharePct = totalRuleAllocatedAvg > 0 ? (avgDAMRuleAllocated / totalRuleAllocatedAvg) * 100 : 0;
  const rtmRuleSharePct = totalRuleAllocatedAvg > 0 ? (avgRTMRuleAllocated / totalRuleAllocatedAvg) * 100 : 0;

  const avgMlGdam = blocksData.length > 0 ? (totalMlGdamVol / blocksData.length) : 0;
  const avgMlDam = 0.0;
  const avgMlRtm = blocksData.length > 0 ? (totalMlRtmVol / blocksData.length) : 0;

  const totalMlAllocatedAvg = avgMlGdam + avgMlDam + avgMlRtm;
  const gdamMlSharePct = totalMlAllocatedAvg > 0 ? (avgMlGdam / totalMlAllocatedAvg) * 100 : 0;
  const damMlSharePct = totalMlAllocatedAvg > 0 ? (avgMlDam / totalMlAllocatedAvg) * 100 : 0;
  const rtmMlSharePct = totalMlAllocatedAvg > 0 ? (avgMlRtm / totalMlAllocatedAvg) * 100 : 0;

  // Group contiguous ML blocks into readable ranges
  const mlRanges = [];
  if (dataRows.length > 0) {
    let currentRange = null;
    dataRows.forEach(row => {
      const market = row.ml_gdam_vol > 0 ? 'GDAM' : 'RTM';
      const rawPct = row.ml_gdam_vol > 0 ? (row.morning_forecast_after_rtc > 0 ? Math.round((row.ml_gdam_vol / row.morning_forecast_after_rtc) * 100) : 100) : 100;
      const pct = Math.min(rawPct, effectiveDaCap);
      if (!currentRange) {
        currentRange = { start_block: row.block_number, end_block: row.block_number, market, pct };
      } else if (currentRange.market === market && currentRange.pct === pct) {
        currentRange.end_block = row.block_number;
      } else {
        mlRanges.push(currentRange);
        currentRange = { start_block: row.block_number, end_block: row.block_number, market, pct };
      }
    });
    if (currentRange) mlRanges.push(currentRange);
  }

  const columnsTab1 = [
    {
      title: 'Block',
      dataIndex: 'block_number',
      width: 60,
      fixed: 'left',
      align: 'center',
    },
    {
      title: 'Time Block',
      dataIndex: 'time_block',
      width: 100,
      align: 'center',
    },
    {
      title: 'Morning Forecast',
      dataIndex: 'morning_forecast_after_rtc',
      width: 150,
      align: 'right',
      render: v => v != null ? Number(v).toFixed(1) : '0.0',
    },
    {
      title: 'GDAM Vol. Allocated',
      dataIndex: 'gdam_vol_allocated',
      width: 160,
      align: 'right',
      render: v => v != null ? Number(v).toFixed(1) : '0.0',
    },
    {
      title: 'GDAM Bid Price (₹)',
      dataIndex: 'gdam_bid_price',
      width: 140,
      align: 'right',
      render: (v, record) => (record.gdam_vol_allocated > 0 && v != null && v > 0) ? Math.round(v).toLocaleString('en-IN') : '0',
    },
    {
      title: 'DAM Vol. Allocated',
      dataIndex: 'dam_vol_allocated',
      width: 160,
      align: 'right',
      render: v => v != null ? Number(v).toFixed(1) : '0.0',
    },
    {
      title: 'DAM Bid Price (₹)',
      dataIndex: 'dam_bid_price',
      width: 140,
      align: 'right',
      render: (v, record) => (record.dam_vol_allocated > 0 && v != null && v > 0) ? Math.round(v).toLocaleString('en-IN') : '0',
    },
  ];

  // --- TAB 2: Allocation Analysis states & handlers ---
  const [dateRange, setDateRange] = useState([dayjs().subtract(3, 'day'), dayjs().subtract(1, 'day')]);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [drillDownDate, setDrillDownDate] = useState('all');

  // --- Performance Analysis (Ongoing Year Trend) states ---
  const [yearlyPerformanceSituations, setYearlyPerformanceSituations] = useState(null);
  const [yearlyPerformanceLoading, setYearlyPerformanceLoading] = useState(false);
  const [yearlyPerformanceRefreshKey, setYearlyPerformanceRefreshKey] = useState(0);
  const yearlyPerformanceCacheRef = React.useRef({});

  // Analysis rules state (completely independent from Tab 1 Day-Ahead modalRules & savedRules)
  const [analysisRules, setAnalysisRules] = useState([
    { key: 1, start_block: 1, end_block: 96, allocation_pct: 100, market: 'GDAM' }
  ]);
  const [savedAnalysisRules, setSavedAnalysisRules] = useState([]);
  const [debouncedAnalysisRules, setDebouncedAnalysisRules] = useState(analysisRules);
  const [analysisRulesLoading, setAnalysisRulesLoading] = useState(false);
  const [analysisRulesSaving, setAnalysisRulesSaving] = useState(false);

  // Fetch saved What-If rules for a plant (independent from Day-Ahead rules)
  const fetchWhatIfRules = async (plantId) => {
    if (!plantId) return;
    setAnalysisRulesLoading(true);
    try {
      const res = await getWhatIfAllocationRules({ plant_id: plantId });
      const rules = res.data?.rules || [];
      if (rules.length > 0) {
        const formatted = rules.map((r, idx) => ({
          key: r.id || (idx + 1),
          start_block: Number(r.start_block),
          end_block: Number(r.end_block),
          allocation_pct: Number(r.allocation_pct),
          market: r.market || 'GDAM'
        }));
        setAnalysisRules(formatted);
        setSavedAnalysisRules(formatted);
        setDebouncedAnalysisRules(formatted);
      } else {
        const defaultRule = [{ key: 1, start_block: 1, end_block: 96, allocation_pct: 100, market: 'GDAM' }];
        setAnalysisRules(defaultRule);
        setSavedAnalysisRules([]);
        setDebouncedAnalysisRules(defaultRule);
      }
    } catch (err) {
      console.error('Error fetching What-If allocation rules:', err);
    } finally {
      setAnalysisRulesLoading(false);
    }
  };

  // Bookmark states
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [selectedBookmarkId, setSelectedBookmarkId] = useState(null);
  const [isBookmarkModalVisible, setIsBookmarkModalVisible] = useState(false);
  const [bookmarkName, setBookmarkName] = useState('');
  const [bookmarkSaving, setBookmarkSaving] = useState(false);

  // Fetch bookmarks
  const fetchBookmarks = async (plantId) => {
    if (!plantId) return;
    setBookmarksLoading(true);
    try {
      const res = await getWhatIfBookmarks({ plant_id: plantId });
      setBookmarks(res.data?.bookmarks || []);
    } catch (err) {
      console.error('Error fetching What-If bookmarks:', err);
    } finally {
      setBookmarksLoading(false);
    }
  };

  const handleSelectBookmark = (bookmarkId) => {
    if (!bookmarkId) {
      setSelectedBookmarkId(null);
      return;
    }
    const b = bookmarks.find(x => x.id === bookmarkId);
    if (!b || !b.rules || b.rules.length === 0) return;

    const formatted = b.rules.map((r, idx) => ({
      key: idx + 1,
      start_block: Number(r.start_block),
      end_block: Number(r.end_block),
      allocation_pct: Number(r.allocation_pct),
      market: r.market || 'GDAM'
    }));

    setAnalysisRules(formatted);
    setDebouncedAnalysisRules(formatted);
    setSelectedBookmarkId(bookmarkId);
    message.success(`Loaded bookmark: "${b.name}"`);
  };

  useEffect(() => {
    if (activeTab === 'analysis' && selectedPlant) {
      fetchWhatIfRules(selectedPlant);
      fetchBookmarks(selectedPlant);
      setSelectedBookmarkId(null);
    }
  }, [activeTab, selectedPlant]);

  // Automatically recalculate scenario allocations when analysis rules are modified
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAnalysisRules(analysisRules.map(r => ({ ...r })));
    }, 400);
    return () => clearTimeout(timer);
  }, [analysisRules]);

  const handleAddAnalysisRule = () => {
    if (analysisRules.length === 0) {
      setAnalysisRules([{ key: 1, start_block: 1, end_block: 96, allocation_pct: 100, market: 'GDAM' }]);
      return;
    }
    const lastRule = analysisRules[analysisRules.length - 1];
    if (lastRule.end_block >= 96) return;
    const nextKey = Math.max(...analysisRules.map(r => r.key)) + 1;
    const nextStart = lastRule.end_block + 1;
    setAnalysisRules([...analysisRules, { key: nextKey, start_block: nextStart, end_block: 96, allocation_pct: 100, market: 'GDAM' }]);
  };

  const handleUpdateAnalysisRule = (key, field, value) => {
    setAnalysisRules(prev => {
      const idx = prev.findIndex(r => r.key === key);
      if (idx === -1) return prev;
      const updated = prev.map(r => ({ ...r }));
      updated[idx][field] = value;
      if (field === 'end_block') {
        if (updated[idx].end_block < updated[idx].start_block) {
          updated[idx].end_block = updated[idx].start_block;
        }
        if (updated[idx].end_block > 96) {
          updated[idx].end_block = 96;
        }
        for (let i = idx + 1; i < updated.length; i++) {
          updated[i].start_block = updated[i - 1].end_block + 1;
          if (updated[i].end_block < updated[i].start_block) {
            updated[i].end_block = Math.min(96, updated[i].start_block);
          }
        }
      }
      return updated.filter(r => r.start_block <= 96);
    });
  };

  const handleDeleteAnalysisRule = (key) => {
    setAnalysisRules(prev => {
      const filtered = prev.filter(r => r.key !== key);
      const updated = filtered.map(r => ({ ...r }));
      for (let i = 0; i < updated.length; i++) {
        if (i === 0) {
          updated[i].start_block = 1;
        } else {
          updated[i].start_block = updated[i - 1].end_block + 1;
        }
        if (updated[i].end_block < updated[i].start_block) {
          updated[i].end_block = Math.min(96, updated[i].start_block);
        }
      }
      return updated.filter(r => r.start_block <= 96);
    });
  };

  const validateAnalysisRules = (rules) => {
    if (!rules || rules.length === 0) {
      return 'Please add at least one allocation rule.';
    }
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (r.start_block == null || r.end_block == null || r.allocation_pct == null || !r.market) {
        return 'All fields (Start Block, End Block, Allocation %, Market Selection) are required for every rule.';
      }
      if (r.start_block < 1 || r.start_block > 96 || r.end_block < 1 || r.end_block > 96) {
        return 'Block ranges must be between 1 and 96.';
      }
      if (r.start_block > r.end_block) {
        return `Rule ${i + 1}: Start block (${r.start_block}) cannot be greater than End block (${r.end_block}).`;
      }
      if (r.allocation_pct < 0 || r.allocation_pct > 100) {
        return 'Allocation percentage must be between 0 and 100.';
      }
    }

    const coveredBlocks = new Array(97).fill(false);
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      for (let b = r.start_block; b <= r.end_block; b++) {
        if (coveredBlocks[b]) {
          return `Overlap detected on Block ${b}. Please ensure block ranges do not overlap.`;
        }
        coveredBlocks[b] = true;
      }
    }
    return null;
  };

  const handleSaveAnalysisRules = async () => {
    if (isReadOnlyMode) {
      message.warning('Read-Only Mode: Modifying allocation rules is disabled.');
      return;
    }
    const errorMsg = validateAnalysisRules(analysisRules);
    if (errorMsg) {
      message.error(errorMsg);
      return;
    }

    setAnalysisRulesSaving(true);
    try {
      const payload = {
        plant_id: selectedPlant || 1,
        rules: analysisRules.map(r => ({
          start_block: r.start_block,
          end_block: r.end_block,
          allocation_pct: r.allocation_pct,
          market: r.market
        }))
      };

      const res = await saveWhatIfAllocationRules(payload);
      if (res.data?.status === 'success') {
        message.success('What-If allocation rules saved successfully!');
        setSavedAnalysisRules(analysisRules.map(r => ({ ...r })));
        setDebouncedAnalysisRules(analysisRules.map(r => ({ ...r })));
      } else {
        message.error('Failed to save What-If allocation rules.');
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || 'Failed to save What-If allocation rules.');
    } finally {
      setAnalysisRulesSaving(false);
    }
  };

  const handleSaveBookmark = async () => {
    const trimmed = bookmarkName.trim();
    if (!trimmed) {
      message.error('Please enter a name for the bookmark.');
      return;
    }
    const errorMsg = validateAnalysisRules(analysisRules);
    if (errorMsg) {
      message.error(errorMsg);
      return;
    }

    setBookmarkSaving(true);
    try {
      const payload = {
        plant_id: selectedPlant || 1,
        name: trimmed,
        rules: analysisRules.map(r => ({
          start_block: r.start_block,
          end_block: r.end_block,
          allocation_pct: r.allocation_pct,
          market: r.market
        }))
      };

      const res = await saveWhatIfBookmark(payload);
      if (res.data?.status === 'success') {
        message.success(res.data.message || `Bookmark "${trimmed}" saved successfully!`);
        setIsBookmarkModalVisible(false);
        setBookmarkName('');
        // Refresh bookmarks list and mark selected
        if (selectedPlant) {
          const fresh = await getWhatIfBookmarks({ plant_id: selectedPlant });
          const list = fresh.data?.bookmarks || [];
          setBookmarks(list);
          if (res.data.bookmark?.id) {
            setSelectedBookmarkId(res.data.bookmark.id);
          }
        }
      } else {
        message.error('Failed to save bookmark.');
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || 'Failed to save bookmark.');
    } finally {
      setBookmarkSaving(false);
    }
  };

  const handleDeleteBookmark = async (bookmarkId, name) => {
    try {
      const res = await deleteWhatIfBookmark({ id: bookmarkId });
      if (res.data?.status === 'success') {
        message.success(`Bookmark "${name}" deleted.`);
        setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
        if (selectedBookmarkId === bookmarkId) {
          setSelectedBookmarkId(null);
        }
      } else {
        message.error('Failed to delete bookmark.');
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || 'Failed to delete bookmark.');
    }
  };

  useEffect(() => {
    setDrillDownDate('all');
  }, [analysisData]);

  // Load plants on mount
  useEffect(() => {
    getPlants().then(res => {
      if (res.data) {
        const plantsList = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setPlants(plantsList);
        if (plantsList.length > 0 && !selectedPlant) {
          setSelectedPlant(plantsList[0].id);
          if (plantsList[0].allocation_mode) {
            setAllocationMode(plantsList[0].allocation_mode);
          }
          if (plantsList[0].auto_submit_mode) {
            setAutoSubmitMode(plantsList[0].auto_submit_mode);
          }
        }
      }
    }).catch(err => {
      console.error('Error fetching plants:', err);
    });
  }, []);

  // Sync allocationMode and autoSubmitMode whenever selectedPlant changes or plants list updates
  useEffect(() => {
    if (!selectedPlant || plants.length === 0) return;
    const currentPlant = plants.find(p => p.id === selectedPlant);
    if (currentPlant) {
      if (currentPlant.allocation_mode) {
        setAllocationMode(currentPlant.allocation_mode);
      }
      if (currentPlant.auto_submit_mode) {
        setAutoSubmitMode(currentPlant.auto_submit_mode);
      }
    }
  }, [selectedPlant, plants]);


  // Fetch / Recalculate analysis
  useEffect(() => {
    if (activeTab !== 'analysis' || !selectedPlant || !dateRange || !dateRange[0] || !dateRange[1]) return;

    let isMounted = true;
    const fetchAnalysis = async () => {
      setAnalysisLoading(true);
      try {
        const payload = {
          plant_id: selectedPlant,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
          what_if_rules: debouncedAnalysisRules.map(r => ({
            start_block: r.start_block,
            end_block: r.end_block,
            allocation_pct: r.allocation_pct,
            market: r.market
          }))
        };
        const res = await getGDAMAllocationAnalysis(payload);
        if (isMounted && res.data) {
          setAnalysisData(res.data);
        }
      } catch (err) {
        console.error('Error in allocation analysis:', err);
        message.error('Failed to compute scenario allocations.');
      } finally {
        if (isMounted) setAnalysisLoading(false);
      }
    };

    fetchAnalysis();
    return () => { isMounted = false; };
  }, [activeTab, selectedPlant, dateRange, debouncedAnalysisRules]);


  // Fetch ongoing year's allocation analysis data dynamically up to yesterday (latest completed date) for Performance Analysis line chart
  useEffect(() => {
    if (activeTab !== 'analysis' || !selectedPlant) return;

    const maxDateStr = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const plantCacheKey = `${selectedPlant}_${maxDateStr}`;
    if (yearlyPerformanceCacheRef.current[plantCacheKey]) {
      setYearlyPerformanceSituations(yearlyPerformanceCacheRef.current[plantCacheKey]);
      return;
    }

    let isMounted = true;
    const fetchYearlyPerformance = async () => {
      setYearlyPerformanceLoading(true);
      try {
        const ongoingYear = dayjs().year();
        const payload = {
          plant_id: selectedPlant,
          start_date: `${ongoingYear}-01-01`,
          end_date: maxDateStr,
          what_if_rules: []
        };
        const res = await getGDAMAllocationAnalysis(payload);
        if (isMounted && res.data?.daily_situations) {
          yearlyPerformanceCacheRef.current[plantCacheKey] = res.data.daily_situations;
          setYearlyPerformanceSituations(res.data.daily_situations);
        }
      } catch (err) {
        console.error('Error fetching yearly performance data:', err);
      } finally {
        if (isMounted) setYearlyPerformanceLoading(false);
      }
    };

    fetchYearlyPerformance();
    return () => { isMounted = false; };
  }, [activeTab, selectedPlant]);

  const availableDates = React.useMemo(() => {
    if (!analysisData || !analysisData.results) return [];
    return Array.from(new Set(analysisData.results.map(r => r.date))).sort();
  }, [analysisData]);

  const filteredBlocksForEvaluation = React.useMemo(() => {
    if (!analysisData || !analysisData.results) return [];
    if (drillDownDate === 'all') {
      return analysisData.results;
    }
    return analysisData.results.filter(r => r.date === drillDownDate);
  }, [analysisData, drillDownDate]);

  const ratioChartData = React.useMemo(() => {
    if (!analysisData || !analysisData.results) return [];
    const blocks = Array.from({ length: 96 }, (_, idx) => ({
      block: idx + 1,
      time_block: '',
      actual_ratio_sum: 0,
      predicted_ratio_sum: 0,
      count: 0
    }));

    const targetResults = drillDownDate === 'all'
      ? analysisData.results
      : analysisData.results.filter(r => r.date === drillDownDate);

    targetResults.forEach(r => {
      const bIdx = r.block - 1;
      if (bIdx >= 0 && bIdx < 96) {
        blocks[bIdx].time_block = r.time_block;
        const actRatio = r.actual_ratio != null ? r.actual_ratio : 0;
        const predRatio = r.predicted_ratio != null ? r.predicted_ratio : 0;
        blocks[bIdx].actual_ratio_sum += actRatio;
        blocks[bIdx].predicted_ratio_sum += predRatio;
        blocks[bIdx].count += 1;
      }
    });

    return blocks.map(b => {
      const cnt = b.count || 1;
      return {
        block: b.block,
        time_block: b.time_block,
        actual_ratio: Number((b.actual_ratio_sum / cnt).toFixed(4)),
        predicted_ratio: Number((b.predicted_ratio_sum / cnt).toFixed(4))
      };
    });
  }, [analysisData, drillDownDate]);

  const metrics = React.useMemo(() => {
    let gdam_gdam = 0;
    let gdam_rtm = 0;
    let rtm_gdam = 0;
    let rtm_rtm = 0;

    filteredBlocksForEvaluation.forEach(r => {
      if (r.actual_ratio == null || r.predicted_ratio == null) return;
      const trueClass = r.actual_ratio > 1.0 ? 'RTM' : 'GDAM';
      const predClass = r.predicted_ratio > 1.0 ? 'RTM' : 'GDAM';

      if (trueClass === 'GDAM' && predClass === 'GDAM') gdam_gdam++;
      else if (trueClass === 'GDAM' && predClass === 'RTM') gdam_rtm++;
      else if (trueClass === 'RTM' && predClass === 'GDAM') rtm_gdam++;
      else if (trueClass === 'RTM' && predClass === 'RTM') rtm_rtm++;
    });

    const total = gdam_gdam + gdam_rtm + rtm_gdam + rtm_rtm;
    const accuracy = total > 0 ? ((gdam_gdam + rtm_rtm) / total) * 100 : 0;

    const rtm_precision = (rtm_rtm + gdam_rtm) > 0 ? (rtm_rtm / (rtm_rtm + gdam_rtm)) * 100 : 0;
    const rtm_recall = (rtm_rtm + rtm_gdam) > 0 ? (rtm_rtm / (rtm_rtm + rtm_gdam)) * 100 : 0;
    const rtm_f1 = (rtm_precision + rtm_recall) > 0 ? 2 * (rtm_precision * rtm_recall) / (rtm_precision + rtm_recall) : 0;

    const gdam_precision = (gdam_gdam + rtm_gdam) > 0 ? (gdam_gdam / (gdam_gdam + rtm_gdam)) * 100 : 0;
    const gdam_recall = (gdam_gdam + gdam_rtm) > 0 ? (gdam_gdam / (gdam_gdam + gdam_rtm)) * 100 : 0;
    const gdam_f1 = (gdam_precision + gdam_recall) > 0 ? 2 * (gdam_precision * gdam_recall) / (gdam_precision + gdam_recall) : 0;

    return {
      gdam_gdam,
      gdam_rtm,
      rtm_gdam,
      rtm_rtm,
      total,
      accuracy,
      rtm: { precision: rtm_precision, recall: rtm_recall, f1: rtm_f1 },
      gdam: { precision: gdam_precision, recall: gdam_recall, f1: gdam_f1 }
    };
  }, [filteredBlocksForEvaluation]);

  const getHeatmapBg = (value, total) => {
    if (total === 0) return '#ffffff';
    const ratio = value / total;
    return `rgba(29, 78, 216, ${Math.max(0.05, Math.min(1.0, ratio * 2.0))})`;
  };

  const getHeatmapTextColor = (value, total) => {
    if (total === 0) return '#1E293B';
    const ratio = value / total;
    return ratio > 0.45 ? '#ffffff' : '#1e3a8a';
  };

  const scenarioComparison = React.useMemo(
    () => buildScenarioComparison(analysisData?.daily_situations),
    [analysisData]
  );
  const [comparisonExpandedKeys, setComparisonExpandedKeys] = useState([]);
  const [comparisonViewMode, setComparisonViewMode] = useState('PU'); // 'PU' | 'Revenue'

  const efficiencyPercent = React.useMemo(() => {
    const act = toNum(scenarioComparison?.totals?.Actual?.['Net Revenue Post DSM']);
    const ideal = toNum(scenarioComparison?.totals?.Ideal?.['Net Revenue Post DSM']);
    if (ideal && ideal > 0 && act != null) {
      return ((act / ideal) * 100).toFixed(1);
    }
    return null;
  }, [scenarioComparison]);

  const scenarioChartData = React.useMemo(() => {
    if (!analysisData || !analysisData.daily_situations || !scenarioComparison?.scenarios?.length) {
      return [];
    }
    const isPU = comparisonViewMode === 'PU';
    const activeTotals = isPU ? scenarioComparison.totalsPU : scenarioComparison.totals;
    const divisor = isPU ? 1 : 1000000;

    return scenarioComparison.scenarios.map(s => {
      const rawNetPostDsm = toNum(activeTotals?.[s]?.['Net Revenue Post DSM']) ?? 0;
      const netPostDsmVal = rawNetPostDsm / divisor;

      return {
        scenario: s,
        label: SCENARIO_LABELS[s] || s,
        netPostDsm: Number(netPostDsmVal.toFixed(3)),
        netPostDsmLabel: netPostDsmVal != null && !isNaN(netPostDsmVal) ? netPostDsmVal.toFixed(2) : '0.00'
      };
    });
  }, [analysisData, scenarioComparison, comparisonViewMode]);

  const CustomScenarioTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const isPU = comparisonViewMode === 'PU';
    const unit = isPU ? '₹ / kWh' : '₹ M';

    return (
      <div style={{
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        padding: '10px 14px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: 700, fontSize: '13px', color: '#1E293B', marginBottom: '6px', borderBottom: '1px solid #F1F5F9', paddingBottom: '4px' }}>
          {d.label} Scenario
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          fontWeight: 600,
          color: '#1E293B'
        }}>
          <span>Net Revenue Post DSM:</span>
          <span style={{ color: '#2563EB', fontWeight: 700 }}>₹ {d.netPostDsm != null ? Number(d.netPostDsm).toFixed(2) : '0.00'} {unit}</span>
        </div>
      </div>
    );
  };

  // Performance Analysis Chart Data (January - December of ongoing year)
  // For the latest ongoing month, only includes completed days with actual generation (dynamically changing as days move ahead).
  const performanceChartData = React.useMemo(() => {
    const today = dayjs();
    const ongoingYear = today.year();
    const maxDateStr = today.subtract(1, 'day').format('YYYY-MM-DD');
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dailySit = yearlyPerformanceSituations || {};

    return months.map((monthName, mIndex) => {
      const monthNum = String(mIndex + 1).padStart(2, '0');
      const monthPrefix = `${ongoingYear}-${monthNum}`;

      const monthDaily = {};
      let hasData = false;

      // Filter days for this month up to yesterday (only completed days with actual generation)
      Object.keys(dailySit).forEach(dateKey => {
        if (dateKey.startsWith(monthPrefix) && dateKey <= maxDateStr) {
          const sit = dailySit[dateKey];
          const downscaledGen = sit?.Actual?.['Actual Downscaled kWh'] ?? sit?.Ideal?.['Actual Downscaled kWh'] ?? 0;
          if (downscaledGen > 0) {
            monthDaily[dateKey] = sit;
            hasData = true;
          }
        }
      });

      let idealPU = null;
      let actualPU = null;

      if (hasData) {
        const monthComparison = buildScenarioComparison(monthDaily);
        const idealVal = monthComparison.totalsPU?.Ideal?.['Net Revenue Post DSM'];
        const actVal = monthComparison.totalsPU?.Actual?.['Net Revenue Post DSM'];

        if (idealVal !== null && idealVal !== undefined && !isNaN(idealVal)) {
          idealPU = Number(Number(idealVal).toFixed(2));
        }
        if (actVal !== null && actVal !== undefined && !isNaN(actVal)) {
          actualPU = Number(Number(actVal).toFixed(2));
        }
      }

      return {
        month: monthName,
        monthShort: dayjs().month(mIndex).format('MMM'),
        monthFull: monthName,
        idealPU,
        actualPU,
        hasData
      };
    });
  }, [yearlyPerformanceSituations]);

  const CustomPerformanceTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const ongoingYear = dayjs().year();

    return (
      <div style={{
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        padding: '10px 14px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        fontSize: '12px',
        minWidth: '200px'
      }}>
        <div style={{ fontWeight: 700, fontSize: '13px', color: '#1E293B', marginBottom: '8px', borderBottom: '1px solid #F1F5F9', paddingBottom: '4px' }}>
          {d.monthFull || label} {ongoingYear}
        </div>
        {!d.hasData ? (
          <div style={{ color: '#94A3B8', fontStyle: 'italic', padding: '4px 0' }}>
            No data available for this month
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#475569', fontWeight: 500 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
                Ideal Scenario:
              </span>
              <span style={{ fontWeight: 700, color: '#10B981' }}>
                {d.idealPU != null ? `₹ ${d.idealPU.toFixed(2)} / kWh` : '-'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#475569', fontWeight: 500 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2563EB', display: 'inline-block' }} />
                Actual Scenario:
              </span>
              <span style={{ fontWeight: 700, color: '#2563EB' }}>
                {d.actualPU != null ? `₹ ${d.actualPU.toFixed(2)} / kWh` : '-'}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleExportToExcel = () => {
    if (!analysisData || !analysisData.daily_situations) return;
    const { scenarios, dateKeys, totals, daily, totalsPU, dailyPU } = scenarioComparison;
    const metricOrder = ['Gross Revenue', ...GROSS_BREAKDOWN_METRICS, 'Charges & Commission', 'Net Revenue', 'DSM Penalty', 'Net Revenue Post DSM'];
    const metricLabel = (m) => (GROSS_BREAKDOWN_METRICS.includes(m) ? `   ${m}` : m);
    const isPU = comparisonViewMode === 'PU';
    const activeTotals = isPU ? totalsPU : totals;
    const activeDaily = isPU ? dailyPU : daily;

    const roundVal = (v, metric) => {
      if (isPU && !PU_SUPPORTED_METRICS.includes(metric)) return '-';
      if (v === null || v === undefined || isNaN(v)) return '-';
      const num = Number(v);
      const isDeduction = metric === 'Charges & Commission' || metric === 'DSM Penalty';
      const finalVal = isDeduction && Math.abs(num) > 0.000001 ? -Math.abs(num) : num;
      return Number(finalVal.toFixed(2));
    };

    // Sheet 1: totals for the selected range (metrics x scenarios)
    const summaryRows = metricOrder.map(metric => {
      const row = { 'Metric': metricLabel(metric) };
      scenarios.forEach(s => { row[SCENARIO_LABELS[s] || s] = roundVal(activeTotals[s]?.[metric], metric); });
      return row;
    });

    // Sheet 2: per-day breakdown
    const dailyRows = [];
    dateKeys.forEach(dk => {
      metricOrder.forEach(metric => {
        const row = { 'Date': dayjs(dk).format('DD-MMM-YYYY'), 'Metric': metricLabel(metric) };
        scenarios.forEach(s => { row[SCENARIO_LABELS[s] || s] = roundVal(activeDaily[dk]?.[s]?.[metric], metric); });
        dailyRows.push(row);
      });
    });

    const workbook = XLSX.utils.book_new();
    const sheetName = isPU ? 'Scenario Comparison (PU)' : 'Scenario Comparison';
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), sheetName);
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dailyRows), 'Daily Breakdown');

    const startStr = dateRange[0] ? dateRange[0].format('YYYYMMDD') : '';
    const endStr = dateRange[1] ? dateRange[1].format('YYYYMMDD') : '';
    const modeTag = isPU ? '_PU' : '_Revenue';
    XLSX.writeFile(workbook, `Scenario_Comparison${modeTag}_${startStr}_${endStr}.xlsx`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', background: '#F8FAFC' }}>
      {/* Header Panel */}
      <div style={{ background: '#fff', padding: '10px 24px 0 24px', borderBottom: '1px solid #E2E8F0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <Title level={4} style={{ margin: 0, color: '#1E293B', fontWeight: 700 }}>Day Ahead Allocation Simulator</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F8FAFC', padding: '3px 8px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
              <Tag color={isAutoSubmissionAllowed ? 'processing' : 'default'} icon={<ClockCircleOutlined />} style={{ borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 600, margin: 0 }}>
                {isAutoSubmissionAllowed ? 'Auto submit 10:30 AM' : 'Auto submission disabled'}
              </Tag>
              {isAutoSubmissionAllowed ? (
                <>
                  <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 600 }}>Mode:</span>
                  <Select
                    size="small"
                    value={autoSubmitMode}
                    onChange={handleAutoSubmitModeChange}
                    style={{ width: 145 }}
                    options={[
                      { value: 'rules', label: 'Manual Allocation' },
                      { value: 'ml', label: 'ML Allocation' },
                    ]}
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Tab Selection */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginTop: '8px', marginBottom: 0 }}
          items={[
            { key: 'day_ahead', label: 'Day-Ahead Allocation' },
            { key: 'analysis', label: 'Allocation Analysis' },
          ]}
        />
      </div>

      <div style={{ flex: 1, padding: '12px 24px', overflowX: 'hidden' }}>
        {isBefore10AmFallback && activeTab !== 'analysis' && (
          <Alert
            message={
              <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                <strong style={{ fontSize: '14px', color: '#92400E' }}>
                  Screen will update at 9:00 AM.
                </strong>{' '}
                Currently showing previous day data ({fallbackDate ? fallbackDate.format('DD-MM-YYYY') : ''}) in read-only mode for target date ({selectedDate ? selectedDate.format('DD-MM-YYYY') : ''}). Please refresh at 9:00 AM to see updated data.
              </div>
            }
            type="warning"
            showIcon
            icon={<ClockCircleOutlined style={{ color: '#D97706', fontSize: '18px' }} />}
            style={{
              marginBottom: 14,
              borderRadius: '8px',
              border: '1px solid #FCD34D',
              background: '#FEF3C7',
              color: '#92400E'
            }}
          />
        )}
        {!canEditBidding && !isBefore10AmFallback && (
          <Alert
            message="Read-Only Mode: You are logged in as a standard user. Actions are disabled."
            type="info"
            showIcon
            style={{ marginBottom: 12, borderRadius: '8px' }}
          />
        )}
        {activeTab === 'day_ahead' ? (
          <Spin spinning={loading} size="large">
            {blocksData.length === 0 ? (
              <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px 0' }}>
                <Empty description={`No ML GDAM Allocation data found.`} />
              </Card>
            ) : (
              <>
                {/* Percentage Changer Row above Table */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text strong style={{ color: '#475569', fontSize: '13px' }}>Delivery Date:</Text>
                      <DatePicker
                        disabled
                        value={selectedDate}
                        onChange={(date) => {
                          if (date) {
                            setSelectedDate(date);
                          }
                        }}
                        allowClear={false}
                        format="YYYY-MM-DD"
                        style={{
                          borderRadius: '6px',
                          border: '1px solid #E5E7EB',
                          fontWeight: 600,
                          color: '#1F2937'
                        }}
                      />
                      {isBefore10AmFallback && fallbackDate && (
                        <Tag color="warning" style={{ fontWeight: 600, fontSize: '12px', padding: '2px 8px' }}>
                          Showing Previous Day Data ({fallbackDate.format('DD-MM-YYYY')})
                        </Tag>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Text style={{ color: '#475569', fontSize: '13px', fontWeight: 500 }}>Average Morning Forecast:</Text>
                      <Text strong style={{ fontSize: '14px', color: '#B7950B' }}>{avgMorningForecast.toFixed(1)} MW</Text>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    {latestSubmissionLog && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          color: '#475569',
                          background: '#F8FAFC',
                          padding: '3px 10px',
                          borderRadius: '6px',
                          border: '1px solid #E2E8F0',
                          lineHeight: '1.4'
                        }}
                      >
                        <ClockCircleOutlined style={{ color: '#0F172A', fontSize: '12px' }} />
                        <span>
                          submitted by <strong style={{ color: '#0F172A' }}>{latestSubmissionLog.submitted_by_username || 'System'}</strong> on{' '}
                          <strong style={{ color: '#0F172A' }}>{dayjs(latestSubmissionLog.submitted_at).format('DD MMM YYYY')}</strong> at{' '}
                          <strong style={{ color: '#0F172A' }}>{dayjs(latestSubmissionLog.submitted_at).format('hh:mm A')}</strong>
                        </span>
                        <Tooltip title="Download Bid Details Excel">
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined style={{ color: '#0052CC', fontSize: '13px' }} />}
                            onClick={() => handleDownloadSubmissionExcel(latestSubmissionLog)}
                            style={{
                              marginLeft: '2px',
                              padding: '0 4px',
                              height: '22px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px'
                            }}
                          />
                        </Tooltip>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          display: 'inline-flex',
                          padding: '3px',
                          background: '#F1F5F9',
                          borderRadius: '8px',
                          border: '1px solid #CBD5E1',
                          gap: '4px'
                        }}
                      >
                        <Button
                          type="text"
                          size="small"
                          onClick={() => handleAllocationModeChange('rules')}
                          style={{
                            height: '32px',
                            padding: '0 14px',
                            borderRadius: '6px',
                            fontWeight: 600,
                            fontSize: '13px',
                            background: allocationMode === 'rules' ? '#10B981' : 'transparent',
                            color: allocationMode === 'rules' ? '#FFFFFF' : '#475569',
                            boxShadow: allocationMode === 'rules' ? '0 2px 4px rgba(16, 185, 129, 0.25)' : 'none',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                        >
                          Manual Allocation
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          onClick={() => handleAllocationModeChange('ml')}
                          style={{
                            height: '32px',
                            padding: '0 14px',
                            borderRadius: '6px',
                            fontWeight: 600,
                            fontSize: '13px',
                            background: allocationMode === 'ml' ? '#10B981' : 'transparent',
                            color: allocationMode === 'ml' ? '#FFFFFF' : '#475569',
                            boxShadow: allocationMode === 'ml' ? '0 2px 4px rgba(16, 185, 129, 0.25)' : 'none',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                        >
                          ML Allocation
                        </Button>
                      </div>
                      <Tooltip
                        title={
                          !isManualSubmissionAllowed
                            ? 'Manual submission is disabled for this plant'
                            : !isSubmitWindowOpen
                              ? 'Submit is only available between 9:00 AM and 10:45 AM'
                              : isReadOnlyMode
                                ? 'Submission is disabled in read-only mode'
                                : ''
                        }
                      >
                        <span>
                          <Button
                            type="primary"
                            onClick={handleFinalSubmit}
                            disabled={isSubmitDisabled}
                            style={{
                              background: isSubmitDisabled ? '#94A3B8' : '#0F172A',
                              borderColor: isSubmitDisabled ? '#94A3B8' : '#0F172A',
                              fontWeight: 600,
                              borderRadius: '6px'
                            }}
                          >
                            Submit
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Inline Override Configuration: 50-50 Split (Left: Bidding Panel, Right: ML Panel) */}
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                  {/* Left 50% Column: Bidding Panel */}
                  <Col xs={24} lg={12}>
                    <Card
                      title="Bidding Panel"
                      size="small"
                      onClick={() => handleAllocationModeChange('rules')}
                      style={{
                        borderRadius: '12px',
                        border: allocationMode === 'rules' ? '2px solid #10B981' : '1px solid #E2E8F0',
                        boxShadow: allocationMode === 'rules' ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.02)',
                        filter: allocationMode === 'rules' ? 'none' : 'blur(1.5px)',
                        opacity: allocationMode === 'rules' ? 1 : 0.75,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        height: '100%'
                      }}
                      bodyStyle={{ padding: '16px 20px' }}
                    >
                      {/* Rule-Based Summary Table */}
                      <div style={{ background: '#F8FAFC', padding: '12px 16px', borderRadius: '10px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', fontWeight: 600, color: '#64748B', fontSize: '12px', paddingBottom: '6px', borderBottom: '1px solid #E2E8F0', marginBottom: '6px' }}>
                          <span>Market</span>
                          <span style={{ textAlign: 'right' }}>Avg MW</span>
                          <span style={{ textAlign: 'right' }}>Share %</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', fontSize: '13px', padding: '3px 0' }}>
                          <span style={{ color: '#117A65', fontWeight: 600 }}>GDAM</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#117A65' }}>{avgGDAMRuleAllocated.toFixed(2)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#117A65' }}>{gdamRuleSharePct.toFixed(1)}%</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', fontSize: '13px', padding: '3px 0' }}>
                          <span style={{ color: '#1B4F72', fontWeight: 600 }}>DAM</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1B4F72' }}>{avgDAMRuleAllocated.toFixed(2)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1B4F72' }}>{damRuleSharePct.toFixed(1)}%</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', fontSize: '13px', padding: '3px 0' }}>
                          <span style={{ color: '#7D3C98', fontWeight: 600 }}>RTM</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#7D3C98' }}>{avgRTMRuleAllocated.toFixed(2)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#7D3C98' }}>{rtmRuleSharePct.toFixed(1)}%</span>
                        </div>
                      </div>

                      <Tabs
                        activeKey={modalActiveTab}
                        onChange={setModalActiveTab}
                        items={[
                          {
                            key: 'allocation',
                            label: 'Override Allocation',
                            children: (
                              <div style={{ paddingTop: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                                  <Button
                                    type="dashed"
                                    onClick={handleAddModalRule}
                                    icon={<PlusOutlined />}
                                    disabled={modalRules.length > 0 && modalRules[modalRules.length - 1].end_block >= 96}
                                    style={{
                                      flex: 1,
                                      ...(isReadOnlyMode ? { opacity: 0.75, pointerEvents: 'none', cursor: 'not-allowed' } : {})
                                    }}
                                  >
                                    Add Rule
                                  </Button>
                                  <Button
                                    type="primary"
                                    onClick={handleSaveOverrideRules}
                                    disabled={isSaveAllocationDisabled}
                                    style={{
                                      background: isSaveAllocationDisabled ? '#94A3B8' : '#0F172A',
                                      borderColor: isSaveAllocationDisabled ? '#94A3B8' : '#0F172A',
                                      fontWeight: 600,
                                      borderRadius: '6px',
                                      cursor: isSaveAllocationDisabled ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    Save Allocation
                                  </Button>
                                </div>

                                {modalRules.length === 0 ? (
                                  <Empty description="No allocation rules defined. Add a rule to start overriding." />
                                ) : (
                                  <div style={{ maxHeight: '350px', overflowY: 'auto', padding: '4px' }}>
                                    <Row gutter={8} style={{ fontWeight: 600, marginBottom: '8px', borderBottom: '1px solid #E2E8F0', paddingBottom: '4px' }}>
                                      <Col span={4}>Start Block</Col>
                                      <Col span={4}>End Block</Col>
                                      <Col span={5}>Allocate To</Col>
                                      <Col span={4}>Allocation %</Col>
                                      <Col span={4}>Price (₹)</Col>
                                      <Col span={3} style={{ textAlign: 'center' }}>Delete</Col>
                                    </Row>
                                    {modalRules.map((rule) => (
                                      <Row key={rule.key} gutter={8} align="middle" style={{ marginBottom: '8px' }}>
                                        <Col span={4}>
                                          <InputNumber
                                            min={1}
                                            max={96}
                                            value={rule.start_block}
                                            disabled
                                            style={{ width: '100%' }}
                                          />
                                        </Col>
                                        <Col span={4}>
                                          <InputNumber
                                            min={1}
                                            max={96}
                                            value={rule.end_block}
                                            onChange={(val) => handleUpdateModalRule(rule.key, 'end_block', val)}
                                            style={{
                                              width: '100%',
                                              ...(isReadOnlyMode ? { opacity: 0.8, pointerEvents: 'none' } : {})
                                            }}
                                          />
                                        </Col>
                                        <Col span={5}>
                                          <Select
                                            value={rule.market}
                                            onChange={(val) => handleUpdateModalRule(rule.key, 'market', val)}
                                            style={{
                                              width: '100%',
                                              ...(isReadOnlyMode ? { opacity: 0.8, pointerEvents: 'none' } : {})
                                            }}
                                          >
                                            <Select.Option value="GDAM">GDAM</Select.Option>
                                            <Select.Option value="DAM">DAM</Select.Option>
                                          </Select>
                                        </Col>
                                        <Col span={4}>
                                          <InputNumber
                                            min={0}
                                            max={100}
                                            value={rule.allocation_pct}
                                            formatter={v => `${v}%`}
                                            parser={v => v.replace('%', '')}
                                            onChange={(val) => handleUpdateModalRule(rule.key, 'allocation_pct', val)}
                                            style={{
                                              width: '100%',
                                              ...(isReadOnlyMode ? { opacity: 0.8, pointerEvents: 'none' } : {})
                                            }}
                                          />
                                        </Col>
                                        <Col span={4}>
                                          <InputNumber
                                            min={0}
                                            value={rule.bid_price}
                                            placeholder="550"
                                            onChange={(val) => handleUpdateModalRule(rule.key, 'bid_price', val)}
                                            style={{
                                              width: '100%',
                                              ...(isReadOnlyMode ? { opacity: 0.8, pointerEvents: 'none' } : {})
                                            }}
                                          />
                                        </Col>
                                        <Col span={3} style={{ textAlign: 'center' }}>
                                          <Button
                                            danger
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            style={isReadOnlyMode ? { opacity: 0.7, pointerEvents: 'none', cursor: 'not-allowed' } : {}}
                                            onClick={() => handleDeleteModalRule(rule.key)}
                                          />
                                        </Col>
                                      </Row>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          },
                          {
                            key: 'bid_category',
                            label: 'Bid Category',
                            children: (
                              <div style={{ padding: '16px 8px' }}>
                                <Row gutter={16}>
                                  <Col xs={24} sm={8}>
                                    <Text strong style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '13px' }}>OCF</Text>
                                    <Select
                                      value={ocf}
                                      disabled={isReadOnlyMode}
                                      onChange={(val) => {
                                        setOcf(val);
                                        if (val === 'No') {
                                          setOcfOption('PREMIUM');
                                          setOcfValue(0);
                                        }
                                      }}
                                      style={{ width: '100%' }}
                                    >
                                      <Select.Option value="No">No</Select.Option>
                                      <Select.Option value="Yes">Yes</Select.Option>
                                    </Select>
                                  </Col>

                                  <Col xs={24} sm={8}>
                                    <Text strong style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '13px' }}>OCF Option</Text>
                                    <Select
                                      value={ocfOption}
                                      onChange={ocf === 'Yes' ? setOcfOption : undefined}
                                      disabled={ocf !== 'Yes'}
                                      style={{ width: '100%' }}
                                    >
                                      <Select.Option value="PREMIUM">Premium</Select.Option>
                                      <Select.Option value="DISCOUNT">Discount</Select.Option>
                                    </Select>
                                  </Col>

                                  <Col xs={24} sm={8}>
                                    <Text strong style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '13px' }}>OCF Value</Text>
                                    <InputNumber
                                      value={ocfValue}
                                      onChange={ocf === 'Yes' ? setOcfValue : undefined}
                                      disabled={ocf !== 'Yes'}
                                      style={{ width: '100%' }}
                                      placeholder="0"
                                      precision={4}
                                    />
                                  </Col>
                                </Row>
                              </div>
                            )
                          }
                        ]}
                      />
                    </Card>
                  </Col>

                  {/* Right 50% Column: ML Panel */}
                  <Col xs={24} lg={12}>
                    <Card
                      title="ML Panel"
                      size="small"
                      onClick={() => handleAllocationModeChange('ml')}
                      style={{
                        borderRadius: '12px',
                        border: allocationMode === 'ml' ? '2px solid #10B981' : '1px solid #E2E8F0',
                        boxShadow: allocationMode === 'ml' ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.02)',
                        filter: allocationMode === 'ml' ? 'none' : 'blur(1.5px)',
                        opacity: allocationMode === 'ml' ? 1 : 0.75,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        height: '100%'
                      }}
                      bodyStyle={{ padding: '16px 20px' }}
                    >
                      {/* ML Summary Table */}
                      <div style={{ background: '#F8FAFC', padding: '12px 16px', borderRadius: '10px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px', fontWeight: 600, color: '#64748B', fontSize: '12px', paddingBottom: '6px', borderBottom: '1px solid #E2E8F0', marginBottom: '6px' }}>
                          <span>Market</span>
                          <span style={{ textAlign: 'right' }}>Win Blocks</span>
                          <span style={{ textAlign: 'right' }}>Avg MW</span>
                          <span style={{ textAlign: 'right' }}>Share %</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px', fontSize: '13px', padding: '3px 0' }}>
                          <span style={{ color: '#117A65', fontWeight: 600 }}>GDAM</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1E293B' }}>{mlGdamBlocks}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#117A65' }}>{avgMlGdam.toFixed(2)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#117A65' }}>{gdamMlSharePct.toFixed(1)}%</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px', fontSize: '13px', padding: '3px 0' }}>
                          <span style={{ color: '#1B4F72', fontWeight: 600 }}>DAM</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1E293B' }}>0</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1B4F72' }}>0.00</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1B4F72' }}>{damMlSharePct.toFixed(1)}%</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px', fontSize: '13px', padding: '3px 0' }}>
                          <span style={{ color: '#7D3C98', fontWeight: 600 }}>RTM</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#1E293B' }}>{mlRtmBlocks}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#7D3C98' }}>{avgMlRtm.toFixed(2)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: '#7D3C98' }}>{rtmMlSharePct.toFixed(1)}%</span>
                        </div>
                      </div>

                      {/* Visual 96-Blocks Distribution Bar */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '6px', fontWeight: 600 }}>
                          96 Blocks Segment Breakdown
                        </div>
                        <div style={{ height: '14px', background: '#E2E8F0', borderRadius: '4px', overflow: 'hidden', display: 'flex', width: '100%' }}>
                          {dataRows.map((row, idx) => {
                            const marketName = row.ml_gdam_vol > 0 ? 'GDAM' : (row.ml_dam_vol > 0 ? 'DAM' : 'RTM');
                            const nextRow = dataRows[idx + 1];
                            const nextMarket = nextRow ? (nextRow.ml_gdam_vol > 0 ? 'GDAM' : (nextRow.ml_dam_vol > 0 ? 'DAM' : 'RTM')) : null;
                            const isSegmentChange = idx < dataRows.length - 1 && marketName !== nextMarket;
                            const color = row.ml_gdam_vol > 0 ? '#10B981' : (row.ml_dam_vol > 0 ? '#3B82F6' : '#8B5CF6');

                            return (
                              <div
                                key={row.block_number}
                                style={{
                                  background: color,
                                  height: '100%',
                                  flex: 1,
                                  borderRight: isSegmentChange ? '2px solid #FFFFFF' : 'none'
                                }}
                                title={`Block ${row.block_number} (${row.time_block}): ${marketName}`}
                              />
                            );
                          })}
                        </div>
                      </div>

                      {/* Plant Master Max Allocation Cap Control */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          background: '#F8FAFC',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          border: '1px solid #E2E8F0',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Text strong style={{ fontSize: '12px', color: '#334155' }}>
                            % of Morning Forecast to be Allocated:
                          </Text>
                        </div>
                        <Space size="small">
                          <InputNumber
                            min={0}
                            max={100}
                            size="small"
                            value={effectiveDaCap}
                            formatter={v => `${v}%`}
                            parser={v => v.replace('%', '')}
                            disabled={isReadOnlyMode}
                            onChange={(val) => {
                              if (val !== null && !isNaN(val)) {
                                setDaAllocationCap(Number(val));
                              }
                            }}
                            onPressEnter={() => handleSaveDaAllocationCap()}
                            style={{ width: '80px', fontWeight: 600 }}
                          />
                          <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveDaAllocationCap();
                            }}
                            loading={isSavingDaCap}
                            disabled={isReadOnlyMode || (currentPlantData?.max_allocation_allowed != null && Number(currentPlantData.max_allocation_allowed) === effectiveDaCap)}
                            style={{
                              background: (isReadOnlyMode || (currentPlantData?.max_allocation_allowed != null && Number(currentPlantData.max_allocation_allowed) === effectiveDaCap)) ? '#94A3B8' : '#0F172A',
                              borderColor: (isReadOnlyMode || (currentPlantData?.max_allocation_allowed != null && Number(currentPlantData.max_allocation_allowed) === effectiveDaCap)) ? '#94A3B8' : '#0F172A',
                              fontWeight: 600,
                              borderRadius: '4px'
                            }}
                          >
                            Save
                          </Button>
                        </Space>
                      </div>

                      {/* ML Suggested Ranges List */}
                      <div>
                        <Text strong style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '8px' }}>Suggested Block Allocation Ranges</Text>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
                          <Row gutter={8} style={{ fontWeight: 600, marginBottom: '6px', borderBottom: '1px solid #E2E8F0', paddingBottom: '4px', fontSize: '12px', color: '#64748B' }}>
                            <Col span={7}>Blocks</Col>
                            <Col span={5}>To</Col>
                            <Col span={5} style={{ textAlign: 'right' }}>Alloc %</Col>
                            <Col span={7} style={{ textAlign: 'right' }}>Price (₹)</Col>
                          </Row>
                          {mlRanges.length === 0 ? (
                            <Text type="secondary" style={{ fontSize: '12px' }}>No ML ranges available.</Text>
                          ) : (
                            mlRanges.map((r, idx) => {
                              const currentPlant = plants.find(p => p.id === selectedPlant);
                              const defaultPrice = r.market === 'GDAM' ? (currentPlant?.gdam_bid_price ?? 551) : (currentPlant?.dam_bid_price ?? 551);
                              const customEntry = mlCustomPrices[r.start_block];
                              const currentVal = customEntry !== undefined ? (typeof customEntry === 'object' ? customEntry.price : customEntry) : defaultPrice;

                              return (
                                <Row key={idx} gutter={8} align="middle" style={{ padding: '6px 0', borderBottom: '1px solid #F1F5F9', fontSize: '13px' }}>
                                  <Col span={7} style={{ fontWeight: 600, color: '#334155' }}>
                                    {r.start_block === r.end_block ? r.start_block : `${r.start_block} - ${r.end_block}`}
                                  </Col>
                                  <Col span={5}>
                                    <Tag color={r.market === 'GDAM' ? 'green' : (r.market === 'DAM' ? 'blue' : 'purple')} style={{ margin: 0, fontWeight: 600 }}>
                                      {r.market}
                                    </Tag>
                                  </Col>
                                  <Col span={5} style={{ textAlign: 'right', fontWeight: 600, color: '#1E293B' }}>
                                    {Math.min(r.pct != null ? r.pct : 0, effectiveDaCap)}%
                                  </Col>
                                  <Col span={7} style={{ textAlign: 'right' }}>
                                    {r.market === 'RTM' ? (
                                      <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
                                    ) : (
                                      <InputNumber
                                        size="small"
                                        min={0}
                                        value={currentVal}
                                        onChange={(val) => {
                                          setMlCustomPrices(prev => ({
                                            ...prev,
                                            [r.start_block]: { price: val, end_block: r.end_block }
                                          }));
                                        }}
                                        style={{
                                          width: '100%',
                                          ...(isReadOnlyMode ? { opacity: 0.8, pointerEvents: 'none' } : {})
                                        }}
                                      />
                                    )}
                                  </Col>
                                </Row>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* 50-50 Split Graphs Section below Panels */}
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                  {/* Left 50% Column: Scheduled Volume Stack (Rule-Based) */}
                  <Col xs={24} lg={12}>
                    <Card
                      title="Scheduled Volume Stack (GDAM, DAM & RTM)"
                      size="small"
                      style={{
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
                        height: '100%'
                      }}
                      bodyStyle={{
                        padding: '16px 20px 8px 20px',
                        minHeight: '400px',
                        background: '#FFFFFF',
                        borderRadius: '0 0 12px 12px'
                      }}
                    >
                      {dataRows.length === 0 ? (
                        <Empty description="No data available for chart" style={{ marginTop: '80px' }} />
                      ) : (
                        <ResponsiveContainer width="100%" height={360}>
                          <AreaChart data={dataRows} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                            <defs>
                              <linearGradient id="colorGdamRule" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0.25} />
                              </linearGradient>
                              <linearGradient id="colorDamRule" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.25} />
                              </linearGradient>
                              <linearGradient id="colorRtmRule" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.25} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                            <XAxis
                              dataKey="block_number"
                              tick={{ fontSize: 10, fill: '#64748B' }}
                              tickLine={false}
                              interval={4}
                              label={{ value: 'Block Number (1 - 96)', position: 'insideBottom', offset: -5, style: { fontSize: '11px', fill: '#64748B', fontWeight: 500 } }}
                            />
                            <YAxis
                              tick={{ fontSize: 11, fill: '#64748B' }}
                              tickLine={false}
                              label={{ value: 'Volume (MW)', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: '11px', fill: '#64748B', fontWeight: 500 } }}
                            />
                            <RechartsTooltip
                              contentStyle={{ background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                              formatter={(value, name) => [`${Number(value).toFixed(1)} MW`, name]}
                              labelFormatter={(label, items) => {
                                const item = items && items[0] && items[0].payload;
                                return item ? `Block ${label} (${item.time_block})` : `Block ${label}`;
                              }}
                            />
                            <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                            <Area
                              type="monotone"
                              dataKey="rule_gdam_vol"
                              name="GDAM Scheduled"
                              stackId="1"
                              stroke="#10B981"
                              strokeWidth={1.5}
                              fillOpacity={1}
                              fill="url(#colorGdamRule)"
                            />
                            <Area
                              type="monotone"
                              dataKey="rule_dam_vol"
                              name="DAM Scheduled"
                              stackId="1"
                              stroke="#3B82F6"
                              strokeWidth={1.5}
                              fillOpacity={1}
                              fill="url(#colorDamRule)"
                            />
                            <Area
                              type="monotone"
                              dataKey="rule_rtm_vol"
                              name="RTM Scheduled"
                              stackId="1"
                              stroke="#8B5CF6"
                              strokeWidth={1.5}
                              fillOpacity={1}
                              fill="url(#colorRtmRule)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </Card>
                  </Col>

                  {/* Right 50% Column: ML Volume Stack */}
                  <Col xs={24} lg={12}>
                    <Card
                      title="ML Volume Stack (GDAM, DAM & RTM)"
                      size="small"
                      style={{
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
                        height: '100%'
                      }}
                      bodyStyle={{
                        padding: '16px 20px 8px 20px',
                        minHeight: '400px',
                        background: '#FFFFFF',
                        borderRadius: '0 0 12px 12px'
                      }}
                    >
                      {dataRows.length === 0 ? (
                        <Empty description="No data available for chart" style={{ marginTop: '80px' }} />
                      ) : (
                        <ResponsiveContainer width="100%" height={360}>
                          <AreaChart data={dataRows} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                            <defs>
                              <linearGradient id="colorGdamMl" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0.25} />
                              </linearGradient>
                              <linearGradient id="colorDamMl" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.25} />
                              </linearGradient>
                              <linearGradient id="colorRtmMl" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.25} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                            <XAxis
                              dataKey="block_number"
                              tick={{ fontSize: 10, fill: '#64748B' }}
                              tickLine={false}
                              interval={4}
                              label={{ value: 'Block Number (1 - 96)', position: 'insideBottom', offset: -5, style: { fontSize: '11px', fill: '#64748B', fontWeight: 500 } }}
                            />
                            <YAxis
                              tick={{ fontSize: 11, fill: '#64748B' }}
                              tickLine={false}
                              label={{ value: 'Volume (MW)', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: '11px', fill: '#64748B', fontWeight: 500 } }}
                            />
                            <RechartsTooltip
                              contentStyle={{ background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                              formatter={(value, name) => [`${Number(value).toFixed(1)} MW`, name]}
                              labelFormatter={(label, items) => {
                                const item = items && items[0] && items[0].payload;
                                return item ? `Block ${label} (${item.time_block})` : `Block ${label}`;
                              }}
                            />
                            <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                            <Area
                              type="monotone"
                              dataKey="ml_gdam_vol"
                              name="GDAM Scheduled"
                              stackId="1"
                              stroke="#10B981"
                              strokeWidth={1.5}
                              fillOpacity={1}
                              fill="url(#colorGdamMl)"
                            />
                            <Area
                              type="monotone"
                              dataKey="ml_dam_vol"
                              name="DAM Scheduled"
                              stackId="1"
                              stroke="#3B82F6"
                              strokeWidth={1.5}
                              fillOpacity={1}
                              fill="url(#colorDamMl)"
                            />
                            <Area
                              type="monotone"
                              dataKey="ml_rtm_vol"
                              name="RTM Scheduled"
                              stackId="1"
                              stroke="#8B5CF6"
                              strokeWidth={1.5}
                              fillOpacity={1}
                              fill="url(#colorRtmMl)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </Card>
                  </Col>
                </Row>

                {/* 96-Block Scrollable Table */}
                <Card
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #E2E8F0',
                    overflow: 'hidden'
                  }}
                  bodyStyle={{ padding: '0px' }}
                >
                  <Table
                    columns={columnsTab1}
                    dataSource={dataRows}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content', y: 500 }}
                    bordered
                    style={{ borderRadius: '12px', overflow: 'hidden' }}
                    rowClassName={(record) => record.block_number % 2 === 0 ? 'even-row' : 'odd-row'}
                  />
                </Card>
              </>
            )}
          </Spin>
        ) : (
          /* --- TAB 2: Allocation Analysis UI --- */
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Date Range Filter */}
            <Card size="small" style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <Text strong style={{ color: '#475569', fontSize: '13px' }}>Date Range</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    disabledDate={current => current && current > dayjs().subtract(1, 'day').endOf('day')}
                    allowClear={false}
                    style={{ minWidth: '280px' }}
                  />
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#1E293B' }}>
                    Efficiency:{' '}
                    <span style={{ color: '#0052CC', fontWeight: 700, fontSize: '15px' }}>
                      {analysisLoading ? '...' : (efficiencyPercent != null ? `${efficiencyPercent}%` : '—')}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748B', fontWeight: 500, marginTop: '-2px' }}>
                    (relative to ideal)
                  </div>
                </div>
              </div>
            </Card>

            {/* Scenario Comparison Table */}
            <Card
              title={<span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>Scenario Comparison</span>}
              extra={
                <Space size="middle">
                  <Segmented
                    value={comparisonViewMode}
                    onChange={setComparisonViewMode}
                    options={[
                      { label: 'PU', value: 'PU' },
                      { label: 'Revenue', value: 'Revenue' }
                    ]}
                    size="small"
                  />
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={handleExportToExcel}
                    disabled={!analysisData || !analysisData.daily_situations}
                  >
                    Export Excel
                  </Button>
                </Space>
              }
              style={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}
              bodyStyle={{ padding: 0 }}
            >
              <Spin spinning={analysisLoading}>
                <Table
                  rowKey="key"
                  dataSource={(() => {
                    const { scenarios, totals, totalsPU } = scenarioComparison;
                    const isPU = comparisonViewMode === 'PU';
                    const activeTotals = isPU ? totalsPU : totals;
                    const makeRow = (metric, extra = {}) => {
                      const row = { key: metric, metric, ...extra };
                      scenarios.forEach(s => { row[s] = activeTotals?.[s]?.[metric] ?? null; });
                      return row;
                    };
                    return TOP_LEVEL_METRICS.map(metric => (
                      metric === 'Gross Revenue'
                        ? makeRow(metric, {
                          children: GROSS_BREAKDOWN_METRICS.map(sub => makeRow(sub, { isChild: true }))
                        })
                        : makeRow(metric)
                    ));
                  })()}
                  expandable={{
                    expandedRowKeys: comparisonExpandedKeys,
                    onExpandedRowsChange: keys => setComparisonExpandedKeys(keys),
                    indentSize: 20
                  }}
                  onRow={record => {
                    const isGrossRevenue = record.metric === 'Gross Revenue';
                    return {
                      onClick: (e) => {
                        if (e.target && e.target.closest && e.target.closest('.ant-table-row-expand-icon')) {
                          return;
                        }
                        if (isGrossRevenue) {
                          setComparisonExpandedKeys(prev =>
                            prev.includes('Gross Revenue')
                              ? prev.filter(k => k !== 'Gross Revenue')
                              : [...prev, 'Gross Revenue']
                          );
                        }
                      },
                      style: {
                        cursor: isGrossRevenue ? 'pointer' : undefined,
                        userSelect: isGrossRevenue ? 'none' : undefined,
                        background: record.metric === 'Net Revenue Post DSM'
                          ? '#F1F5F9'
                          : record.isChild ? '#FCFDFE' : undefined
                      }
                    };
                  }}
                  columns={(() => {
                    const hasData = Boolean(analysisData?.daily_situations && scenarioComparison.dateKeys.length > 0);
                    const isPU = comparisonViewMode === 'PU';
                    const cols = [
                      {
                        title: 'Metric',
                        dataIndex: 'metric',
                        key: 'metric',
                        width: 240,
                        onHeaderCell: () => ({ style: { textAlign: 'center' } }),
                        render: (v, record) => (
                          <span style={{
                            color: record.isChild ? '#64748B' : '#1E293B',
                            fontSize: record.isChild ? '12px' : '13px',
                            fontWeight: record.isChild ? 500 : (v === 'Net Revenue Post DSM' ? 800 : 700)
                          }}>
                            {v}
                          </span>
                        )
                      }
                    ];
                    scenarioComparison.scenarios.forEach(s => {
                      cols.push({
                        title: (
                          <div style={{ textAlign: 'center' }}>
                            <span style={{ fontWeight: 700, color: '#1E293B' }}>{SCENARIO_LABELS[s] || s}</span>
                            {isPU && (
                              <div style={{ fontSize: '11px', fontWeight: 500, color: '#64748B' }}>₹ / kWh</div>
                            )}
                          </div>
                        ),
                        dataIndex: s,
                        key: s,
                        align: 'center',
                        width: 180,
                        render: (val, record) => {
                          const isDeduction = record.metric === 'Charges & Commission' || record.metric === 'DSM Penalty';
                          if (isPU) {
                            if (!PU_SUPPORTED_METRICS.includes(record.metric)) {
                              return <span style={{ color: '#94A3B8' }}>-</span>;
                            }
                            if (!hasData || val === null || val === undefined || isNaN(val)) {
                              return <span style={{ color: '#94A3B8' }}>-</span>;
                            }
                            const isNegative = isDeduction ? Math.abs(val) > 0.000001 : val < 0;
                            return (
                              <span style={{
                                display: 'inline-block',
                                textAlign: 'center',
                                fontSize: record.isChild ? '12px' : '12.5px',
                                fontWeight: record.isChild ? 400 : (record.metric === 'Net Revenue Post DSM' ? 700 : 600),
                                color: isNegative ? '#EF4444' : '#1E293B',
                                fontVariantNumeric: 'tabular-nums'
                              }}>
                                {formatPUValue(val, record.metric)}
                              </span>
                            );
                          }

                          if (!hasData || val === null || val === undefined || isNaN(val)) {
                            return <span style={{ color: '#94A3B8' }}>-</span>;
                          }
                          const isNegative = isDeduction ? Math.abs(val) > 0.000001 : val < 0;
                          return (
                            <span style={{
                              display: 'inline-block',
                              textAlign: 'center',
                              fontSize: record.isChild ? '12px' : '12.5px',
                              fontWeight: record.isChild ? 400 : 600,
                              color: isNegative ? '#EF4444' : (record.isChild ? '#475569' : '#1E293B'),
                              fontVariantNumeric: 'tabular-nums'
                            }}>
                              {formatComparisonValue(val, record.metric)}
                            </span>
                          );
                        }
                      });
                    });
                    return cols;
                  })()}
                  pagination={false}
                  bordered
                  size="small"
                  scroll={{ x: 'max-content' }}
                  footer={scenarioComparison.dateKeys.length > 1
                    ? () => (
                      <span style={{ fontSize: '11.5px', color: '#64748B' }}>
                        Totals across {scenarioComparison.dateKeys.length} days
                        ({dayjs(scenarioComparison.dateKeys[0]).format('DD-MMM-YYYY')} – {dayjs(scenarioComparison.dateKeys[scenarioComparison.dateKeys.length - 1]).format('DD-MMM-YYYY')}).
                        Export Excel for the daily breakdown.
                      </span>
                    )
                    : undefined}
                />
              </Spin>
            </Card>

            {/* 50-50 Split: Scenario Comparison Breakdown Chart & What-If Allocation Rules */}
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} lg={12}>
                <Card
                  title={<span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>Net Revenue Post DSM</span>}
                  extra={
                    <Tag color={comparisonViewMode === 'PU' ? 'blue' : 'purple'} style={{ borderRadius: '4px', fontWeight: 600 }}>
                      {comparisonViewMode === 'PU' ? 'PU (₹/kWh)' : 'Revenue (₹ M)'}
                    </Tag>
                  }
                  style={{ borderRadius: '12px', border: '1px solid #E2E8F0', height: '100%' }}
                  bodyStyle={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', height: 'calc(100% - 57px)' }}
                >
                  <Spin spinning={analysisLoading}>
                    {Boolean(analysisData?.daily_situations && scenarioComparison.dateKeys.length > 0 && scenarioChartData.length > 0) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                        <div style={{ width: '100%', height: 320 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={scenarioChartData}
                              margin={{ top: 20, right: 20, left: -5, bottom: 5 }}
                              barCategoryGap="25%"
                            >
                              <defs>
                                <linearGradient id="netPostDsmBarGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.95} />
                                  <stop offset="100%" stopColor="#1D4ED8" stopOpacity={0.85} />
                                </linearGradient>
                              </defs>

                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                              <XAxis
                                dataKey="label"
                                tickLine={false}
                                axisLine={{ stroke: '#CBD5E1' }}
                                tick={{ fill: '#64748B', fontSize: 12.5, fontWeight: 600 }}
                              />
                              <YAxis
                                tickLine={false}
                                axisLine={{ stroke: '#CBD5E1' }}
                                tick={{ fill: '#64748B', fontSize: 11.5 }}
                                label={{
                                  value: comparisonViewMode === 'PU' ? '₹ / kWh' : '₹ M',
                                  angle: -90,
                                  position: 'insideLeft',
                                  fill: '#64748B',
                                  fontSize: 11.5,
                                  offset: 8
                                }}
                              />
                              <RechartsTooltip content={<CustomScenarioTooltip />} />

                              <Bar
                                dataKey="netPostDsm"
                                name="Net Revenue Post DSM"
                                fill="url(#netPostDsmBarGrad)"
                                maxBarSize={48}
                                radius={[4, 4, 0, 0]}
                              >
                                <LabelList
                                  dataKey="netPostDsmLabel"
                                  position="top"
                                  offset={6}
                                  style={{ fill: '#1E293B', fontWeight: 700, fontSize: '11.5px' }}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Chart Legend */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px',
                          paddingTop: '10px',
                          fontSize: '12px',
                          color: '#475569'
                        }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: 12, height: 12, background: '#2563EB', borderRadius: 2, display: 'inline-block' }} />
                            <span style={{ fontWeight: 600 }}>Net Revenue Post DSM</span>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '60px 0', textAlign: 'center' }}>
                        <Empty description="No comparison data available for chart" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      </div>
                    )}
                  </Spin>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                {/* What-If Rules Panel */}
                <Card
                  title={<span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>What-If Allocation Rules</span>}
                  // extra={
                  //   savedAnalysisRules.length > 0 ? (
                  //     <Tag color="cyan" style={{ borderRadius: '4px', fontWeight: 600 }}>
                  //       {savedAnalysisRules.length} Saved {savedAnalysisRules.length === 1 ? 'Rule' : 'Rules'}
                  //     </Tag>
                  //   ) : null
                  // }
                  style={{ borderRadius: '12px', border: '1px solid #E2E8F0', height: '100%' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                    <Space wrap size="small">
                      <Button
                        type="dashed"
                        onClick={handleAddAnalysisRule}
                        icon={<PlusOutlined />}
                        disabled={analysisRules.length > 0 && analysisRules[analysisRules.length - 1].end_block >= 96}
                      >
                        Add Rule
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => setDebouncedAnalysisRules(analysisRules.map(r => ({ ...r })))}
                        loading={analysisLoading}
                        icon={<ReloadOutlined />}
                        style={{ background: '#2563EB', borderColor: '#2563EB', fontWeight: 600 }}
                      >
                        Recalculate
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleSaveAnalysisRules}
                        loading={analysisRulesSaving}
                        style={{
                          background: '#0F172A',
                          borderColor: '#0F172A',
                          fontWeight: 600,
                          borderRadius: '6px'
                        }}
                      >
                        Save Rules
                      </Button>
                    </Space>

                    <Space wrap size="small">
                      <Select
                        placeholder="Load Bookmark..."
                        style={{ width: 195 }}
                        value={selectedBookmarkId}
                        onChange={handleSelectBookmark}
                        loading={bookmarksLoading}
                        allowClear
                        onClear={() => setSelectedBookmarkId(null)}
                        options={bookmarks.map(b => ({
                          value: b.id,
                          label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 135 }} title={b.name}>
                                {b.name}
                              </span>
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined style={{ fontSize: '11px' }} />}
                                style={{ width: 20, height: 20, minWidth: 20, padding: 0 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteBookmark(b.id, b.name);
                                }}
                              />
                            </div>
                          )
                        }))}
                      />
                      <Button
                        onClick={() => setIsBookmarkModalVisible(true)}
                        style={{
                          borderColor: '#6366F1',
                          color: '#6366F1',
                          fontWeight: 600,
                          borderRadius: '6px'
                        }}
                      >
                        Bookmark
                      </Button>
                    </Space>
                  </div>

                  {/* Bookmark Save Modal */}
                  <Modal
                    title={<span style={{ fontWeight: 700, color: '#1E293B' }}>Save Rules as Bookmark</span>}
                    open={isBookmarkModalVisible}
                    onOk={handleSaveBookmark}
                    onCancel={() => {
                      setIsBookmarkModalVisible(false);
                      setBookmarkName('');
                    }}
                    confirmLoading={bookmarkSaving}
                    okText="Save Bookmark"
                    okButtonProps={{ style: { background: '#6366F1', borderColor: '#6366F1', fontWeight: 600 } }}
                    destroyOnClose
                  >
                    <div style={{ padding: '12px 0' }}>
                      <Input
                        placeholder="Bookmark Name"
                        value={bookmarkName}
                        onChange={(e) => setBookmarkName(e.target.value)}
                        maxLength={100}
                        onPressEnter={handleSaveBookmark}
                        autoFocus
                      />
                    </div>
                  </Modal>

                  <Spin spinning={analysisRulesLoading}>
                    {analysisRules.length === 0 ? (
                      <Empty description="No allocation rules defined. Add a rule to start." />
                    ) : (
                      <div style={{ maxHeight: '350px', overflowY: 'auto', padding: '4px' }}>
                        <Row gutter={8} style={{ fontWeight: 600, marginBottom: '8px', borderBottom: '1px solid #E2E8F0', paddingBottom: '4px', color: '#475569', fontSize: '12px' }}>
                          <Col span={5}>Start Block</Col>
                          <Col span={5}>End Block</Col>
                          <Col span={6}>Allocate To</Col>
                          <Col span={5}>Allocation %</Col>
                          <Col span={3} style={{ textAlign: 'center' }}>Delete</Col>
                        </Row>
                        {analysisRules.map((rule) => (
                          <Row key={rule.key} gutter={8} align="middle" style={{ marginBottom: '8px' }}>
                            <Col span={5}>
                              <InputNumber
                                min={1}
                                max={96}
                                value={rule.start_block}
                                disabled
                                style={{ width: '100%' }}
                              />
                            </Col>
                            <Col span={5}>
                              <InputNumber
                                min={1}
                                max={96}
                                value={rule.end_block}
                                onChange={(val) => handleUpdateAnalysisRule(rule.key, 'end_block', val)}
                                style={{ width: '100%' }}
                              />
                            </Col>
                            <Col span={6}>
                              <Select
                                value={rule.market}
                                onChange={(val) => handleUpdateAnalysisRule(rule.key, 'market', val)}
                                style={{ width: '100%' }}
                              >
                                <Select.Option value="GDAM">GDAM</Select.Option>
                                <Select.Option value="DAM">DAM</Select.Option>
                              </Select>
                            </Col>
                            <Col span={5}>
                              <InputNumber
                                min={0}
                                max={100}
                                value={rule.allocation_pct}
                                formatter={v => `${v}%`}
                                parser={v => v.replace('%', '')}
                                onChange={(val) => handleUpdateAnalysisRule(rule.key, 'allocation_pct', val)}
                                style={{ width: '100%' }}
                              />
                            </Col>
                            <Col span={3} style={{ textAlign: 'center' }}>
                              <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteAnalysisRule(rule.key)}
                              />
                            </Col>
                          </Row>
                        ))}
                      </div>
                    )}
                  </Spin>
                </Card>
              </Col>
            </Row>

            {/* Performance Analysis Line Chart */}
            <Card
              title={
                <span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>
                  Performance Analysis
                </span>
              }
              style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <Spin spinning={yearlyPerformanceLoading} tip="Loading monthly performance data...">
                {performanceChartData.some(d => d.hasData) || yearlyPerformanceLoading ? (
                  <div style={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={performanceChartData}
                        margin={{ top: 20, right: 30, left: 15, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis
                          dataKey="month"
                          interval={0}
                          tickLine={false}
                          axisLine={{ stroke: '#CBD5E1' }}
                          tick={{ fill: '#64748B', fontSize: 11, fontWeight: 600 }}
                        />
                        <YAxis
                          tickLine={false}
                          axisLine={{ stroke: '#CBD5E1' }}
                          tick={{ fill: '#64748B', fontSize: 11.5 }}
                          domain={['auto', 'auto']}
                          tickFormatter={(v) => `₹ ${Number(v).toFixed(1)}`}
                          label={{
                            value: 'Per-unit Net Revenue Post DSM (₹ / kWh)',
                            angle: -90,
                            position: 'insideLeft',
                            fill: '#64748B',
                            fontSize: 11.5,
                            offset: 0,
                            style: { textAnchor: 'middle' }
                          }}
                        />
                        <RechartsTooltip content={<CustomPerformanceTooltip />} />
                        <Legend
                          verticalAlign="bottom"
                          align="center"
                          iconType="circle"
                          wrapperStyle={{ paddingTop: '16px' }}
                          formatter={(value) => (
                            <span style={{ color: '#1E293B', fontWeight: 600, fontSize: '12px', marginRight: '16px' }}>
                              {value}
                            </span>
                          )}
                        />
                        <Line
                          type="monotone"
                          dataKey="idealPU"
                          name="Ideal Scenario (₹/kWh)"
                          stroke="#10B981"
                          strokeWidth={2.5}
                          dot={{ r: 4, fill: '#10B981', strokeWidth: 2, stroke: '#FFFFFF' }}
                          activeDot={{ r: 6, fill: '#10B981', stroke: '#FFFFFF', strokeWidth: 2 }}
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="actualPU"
                          name="Actual Scenario (₹/kWh)"
                          stroke="#2563EB"
                          strokeWidth={2.5}
                          dot={{ r: 4, fill: '#2563EB', strokeWidth: 2, stroke: '#FFFFFF' }}
                          activeDot={{ r: 6, fill: '#2563EB', stroke: '#FFFFFF', strokeWidth: 2 }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ padding: '60px 0', textAlign: 'center' }}>
                    <Empty description="No yearly performance data available for this plant." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                )}
              </Spin>
            </Card>

            {/* Drill-down Selector */}
            <Card size="small" style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <span style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px', display: 'block' }}>Model Performance & Analysis Drill-down</span>
                  <span style={{ color: '#64748B', fontSize: '12px' }}>Analyze model accuracy, confusion matrix, and prediction ratios for the selected period.</span>
                </div>
                <Space align="center">
                  <span style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Selected Date:</span>
                  <Select
                    value={drillDownDate}
                    onChange={setDrillDownDate}
                    style={{ width: 180 }}
                    size="small"
                  >
                    <Select.Option value="all">Average of All Days</Select.Option>
                    {availableDates.map(d => (
                      <Select.Option key={d} value={d}>{dayjs(d).format('DD-MMM-YYYY')}</Select.Option>
                    ))}
                  </Select>
                </Space>
              </div>
            </Card>

            {/* Confusion Matrix and Model Performance Metrics Row */}
            <Spin spinning={analysisLoading} tip="Loading analysis data...">
              <Row gutter={[20, 20]}>
                {/* Left: Confusion Matrix Box */}
                <Col xs={24} md={12}>
                  <Card
                    title={<span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>Confusion Matrix ({drillDownDate === 'all' ? 'All Selected Days' : dayjs(drillDownDate).format('DD-MMM-YYYY')})</span>}
                    style={{ borderRadius: '12px', border: '1px solid #E2E8F0', height: '100%' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', minHeight: '180px', overflowX: 'auto' }}>
                      <div style={{
                        writingMode: 'vertical-lr',
                        transform: 'rotate(180deg)',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: '#64748B',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        True Label
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
                          Predicted Label
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', marginLeft: '50px', marginBottom: '4px' }}>
                            <div style={{ width: '70px', textAlign: 'center', fontWeight: 700, fontSize: '11px', color: '#475569' }}>GDAM</div>
                            <div style={{ width: '70px', textAlign: 'center', fontWeight: 700, fontSize: '11px', color: '#475569' }}>RTM</div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: '50px', fontWeight: 700, fontSize: '11px', color: '#475569', textAlign: 'right', paddingRight: '8px' }}>GDAM</div>
                            <div style={{
                              width: '70px', height: '50px',
                              background: getHeatmapBg(metrics.gdam_gdam, metrics.total),
                              border: '1px solid #cbd5e1',
                              display: 'flex', justifyContent: 'center', alignItems: 'center',
                              fontWeight: 800, fontSize: '16px',
                              color: getHeatmapTextColor(metrics.gdam_gdam, metrics.total),
                              transition: 'all 0.3s ease'
                            }}>
                              {metrics.gdam_gdam}
                            </div>
                            <div style={{
                              width: '70px', height: '50px',
                              background: getHeatmapBg(metrics.gdam_rtm, metrics.total),
                              border: '1px solid #cbd5e1', borderLeft: 'none',
                              display: 'flex', justifyContent: 'center', alignItems: 'center',
                              fontWeight: 800, fontSize: '16px',
                              color: getHeatmapTextColor(metrics.gdam_rtm, metrics.total),
                              transition: 'all 0.3s ease'
                            }}>
                              {metrics.gdam_rtm}
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: '50px', fontWeight: 700, fontSize: '11px', color: '#475569', textAlign: 'right', paddingRight: '8px' }}>RTM</div>
                            <div style={{
                              width: '70px', height: '50px',
                              background: getHeatmapBg(metrics.rtm_gdam, metrics.total),
                              border: '1px solid #cbd5e1', borderTop: 'none',
                              display: 'flex', justifyContent: 'center', alignItems: 'center',
                              fontWeight: 800, fontSize: '16px',
                              color: getHeatmapTextColor(metrics.rtm_gdam, metrics.total),
                              transition: 'all 0.3s ease'
                            }}>
                              {metrics.rtm_gdam}
                            </div>
                            <div style={{
                              width: '70px', height: '50px',
                              background: getHeatmapBg(metrics.rtm_rtm, metrics.total),
                              border: '1px solid #cbd5e1', borderTop: 'none', borderLeft: 'none',
                              display: 'flex', justifyContent: 'center', alignItems: 'center',
                              fontWeight: 800, fontSize: '16px',
                              color: getHeatmapTextColor(metrics.rtm_rtm, metrics.total),
                              transition: 'all 0.3s ease'
                            }}>
                              {metrics.rtm_rtm}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
                        <div style={{
                          width: '10px', height: '100px',
                          background: 'linear-gradient(to top, rgba(29, 78, 216, 0.05) 0%, rgba(29, 78, 216, 1) 100%)',
                          borderRadius: '2px', border: '1px solid #cbd5e1'
                        }}></div>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100px', fontSize: '9px', fontWeight: 600, color: '#64748b', marginLeft: '4px' }}>
                          <span>Max</span>
                          <span>Min</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>

                {/* Right: Model Performance Metrics Box */}
                <Col xs={24} md={12}>
                  <Card
                    title={<span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>Model Performance Metrics ({drillDownDate === 'all' ? 'All Selected Days' : dayjs(drillDownDate).format('DD-MMM-YYYY')})</span>}
                    style={{ borderRadius: '12px', border: '1px solid #E2E8F0', height: '100%' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '76px', height: '76px', borderRadius: '50%',
                          background: '#E8F5E9',
                          display: 'flex', justifyContent: 'center', alignItems: 'center',
                          border: '3px solid #2E7D32', flexShrink: 0
                        }}>
                          <span style={{ fontSize: '16px', fontWeight: 800, color: '#2E7D32' }}>
                            {metrics.accuracy.toFixed(2)}%
                          </span>
                        </div>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '14px', color: '#1E293B', display: 'block' }}>Accuracy</span>
                        </div>
                      </div>

                      <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: '12px' }}>
                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #E2E8F0', color: '#64748B', fontWeight: 600 }}>
                              <th style={{ textAlign: 'left', paddingBottom: '6px' }}>Class</th>
                              <th style={{ textAlign: 'right', paddingBottom: '6px' }}>Precision</th>
                              <th style={{ textAlign: 'right', paddingBottom: '6px' }}>Recall</th>
                              <th style={{ textAlign: 'right', paddingBottom: '6px' }}>F1-Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ borderBottom: '1px dashed #F1F5F9' }}>
                              <td style={{ padding: '6px 0', fontWeight: 600, color: '#1E293B' }}>GDAM</td>
                              <td style={{ textAlign: 'right', color: '#334155' }}>{metrics.gdam.precision.toFixed(2)}%</td>
                              <td style={{ textAlign: 'right', color: '#334155' }}>{metrics.gdam.recall.toFixed(2)}%</td>
                              <td style={{ textAlign: 'right', color: '#334155' }}>{metrics.gdam.f1.toFixed(2)}%</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '6px 0', fontWeight: 600, color: '#1E293B' }}>RTM</td>
                              <td style={{ textAlign: 'right', color: '#334155' }}>{metrics.rtm.precision.toFixed(2)}%</td>
                              <td style={{ textAlign: 'right', color: '#334155' }}>{metrics.rtm.recall.toFixed(2)}%</td>
                              <td style={{ textAlign: 'right', color: '#334155' }}>{metrics.rtm.f1.toFixed(2)}%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Predicted vs Actual Ratio Chart */}
              <Card
                title={<span style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>Predicted vs Actual Ratio ({drillDownDate === 'all' ? 'Averaged' : dayjs(drillDownDate).format('DD-MMM-YYYY')})</span>}
                style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}
              >
                <div style={{ width: '100%', height: '280px', position: 'relative' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ratioChartData} margin={{ top: 10, right: 10, left: -25, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="block" label={{ value: 'Block', position: 'insideBottom', offset: -2, fontSize: 9, fill: '#64748B', fontWeight: 600 }} tick={{ fontSize: 9 }} stroke="#94A3B8" />
                      <YAxis tick={{ fontSize: 9 }} stroke="#94A3B8" domain={[0, 2]} ticks={[0.0, 0.5, 1.0, 1.5, 2.0]} label={{ value: 'RTM / G-DAM Ratio', angle: -90, position: 'insideLeft', offset: 12, fontSize: 9, fill: '#64748B', fontWeight: 600 }} />
                      <RechartsTooltip labelFormatter={(label) => `Block: ${label}`} formatter={(v) => [v.toFixed(4), 'Ratio']} />
                      <Legend wrapperStyle={{ fontSize: 9 }} />
                      <Line type="monotone" dataKey="actual_ratio" name="Actual Ratio" stroke="green" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="predicted_ratio" name="Predicted Ratio" stroke="#E67E22" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      <ReferenceLine y={1.0} stroke="#EF4444" strokeDasharray="3 3" label={{ position: 'top', fill: '#EF4444', fontSize: 9, fontWeight: 600 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Spin>
          </Space>
        )}
      </div>
    </div>
  );
}